import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// ─────────────────────────────────────────────────────────────────────────────
// TAX CALCULATOR — DATA
// ─────────────────────────────────────────────────────────────────────────────

const BRACKETS = {
  single: [{rate:0.10,min:0,max:11600},{rate:0.12,min:11600,max:47150},{rate:0.22,min:47150,max:100525},{rate:0.24,min:100525,max:191950},{rate:0.32,min:191950,max:243725},{rate:0.35,min:243725,max:609350},{rate:0.37,min:609350,max:Infinity}],
  mfj:    [{rate:0.10,min:0,max:23200},{rate:0.12,min:23200,max:94300},{rate:0.22,min:94300,max:201050},{rate:0.24,min:201050,max:383900},{rate:0.32,min:383900,max:487450},{rate:0.35,min:487450,max:731200},{rate:0.37,min:731200,max:Infinity}],
  mfs:    [{rate:0.10,min:0,max:11600},{rate:0.12,min:11600,max:47150},{rate:0.22,min:47150,max:100525},{rate:0.24,min:100525,max:191950},{rate:0.32,min:191950,max:243725},{rate:0.35,min:243725,max:365600},{rate:0.37,min:365600,max:Infinity}],
  hoh:    [{rate:0.10,min:0,max:16550},{rate:0.12,min:16550,max:63100},{rate:0.22,min:63100,max:100500},{rate:0.24,min:100500,max:191950},{rate:0.32,min:191950,max:243700},{rate:0.35,min:243700,max:609350},{rate:0.37,min:609350,max:Infinity}],
};
const STANDARD_DEDUCTIONS = { single:14600, mfj:29200, mfs:14600, hoh:21900 };
const STATE_RATES = {'0':0,AL:0.05,AK:0,AZ:0.025,AR:0.039,CA:0.093,CO:0.044,CT:0.055,DE:0.055,FL:0,GA:0.0549,HI:0.082,ID:0.058,IL:0.0495,IN:0.03,IA:0.055,KS:0.057,KY:0.04,LA:0.04,ME:0.065,MD:0.0525,MA:0.05,MI:0.0425,MN:0.075,MS:0.047,MO:0.047,MT:0.059,NE:0.05,NV:0,NH:0,NJ:0.063,NM:0.049,NY:0.085,NC:0.045,ND:0.025,OH:0.0399,OK:0.0475,OR:0.09,PA:0.0307,RI:0.055,SC:0.062,SD:0,TN:0,TX:0,UT:0.0465,VT:0.066,VA:0.0575,WA:0,WV:0.065,WI:0.065,WY:0};
const SS_RATE=0.062,SS_LIMIT=168600,MEDICARE_RATE=0.0145,ADDL_MEDICARE_RATE=0.009;
const ADDL_MEDICARE_THRESH={single:200000,mfj:250000,mfs:125000,hoh:200000};
const fmt=(n)=>"$"+Math.round(n).toLocaleString();

function calcBrackets(taxable,filing){
  let tax=0,rows=[];
  for(let b of BRACKETS[filing]){
    if(taxable<=b.min)break;
    const portion=Math.min(taxable,b.max)-b.min,t=portion*b.rate;
    tax+=t;rows.push({rate:b.rate,portion,tax:t});
  }
  return{tax,rows};
}

function TaxCalculator({onBack}){
  const[filing,setFiling]=useState("single");
  const[deductionType,setDeductionType]=useState("standard");
  const[result,setResult]=useState(null);
  const[form,setForm]=useState({income:75000,otherIncome:0,retirement:0,hsa:0,credits:0,pretax:0,itemized:0,state:"0",wFederal:0,wState:0,wSS:0,wMedicare:0});
  const set=(key,val)=>setForm(f=>({...f,[key]:parseFloat(val)||0}));
  const calculate=()=>{
    const{income,otherIncome,retirement,hsa,credits,pretax,itemized,state,wFederal,wState,wSS,wMedicare}=form;
    const stateRate=STATE_RATES[state]||0,grossIncome=income+otherIncome,adjustments=retirement+hsa+pretax;
    const agi=Math.max(0,grossIncome-adjustments),stdDed=STANDARD_DEDUCTIONS[filing];
    const deduction=deductionType==="itemized"?Math.max(itemized,stdDed):stdDed;
    const taxableIncome=Math.max(0,agi-deduction);
    const{tax:fedTaxRaw,rows:bracketRows}=calcBrackets(taxableIncome,filing);
    const fedTax=Math.max(0,fedTaxRaw-credits),ssTax=Math.min(income,SS_LIMIT)*SS_RATE;
    const medicareTax=income*MEDICARE_RATE;
    const addlMedicare=income>ADDL_MEDICARE_THRESH[filing]?(income-ADDL_MEDICARE_THRESH[filing])*ADDL_MEDICARE_RATE:0;
    const stateTax=agi*stateRate,totalTax=fedTax+ssTax+medicareTax+addlMedicare+stateTax;
    const takeHome=grossIncome-totalTax-adjustments,effectiveRate=grossIncome>0?(totalTax/grossIncome*100):0;
    const marginalRate=bracketRows.length>0?bracketRows[bracketRows.length-1].rate*100:0;
    const totalWithheld=wFederal+wState+wSS+wMedicare,netDiff=totalWithheld-totalTax;
    setResult({grossIncome,adjustments,agi,deduction,taxableIncome,fedTax,ssTax,medicareTax,addlMedicare,stateTax,totalTax,takeHome,effectiveRate,marginalRate,bracketRows,totalWithheld,netDiff,deductionType,wFederal,wState,wSS,wMedicare});
  };
  const card={background:"#ffffff",border:"1px solid #e2e8f0",borderRadius:12,padding:"1.8rem",marginBottom:"1.5rem",boxShadow:"0 1px 3px rgba(0,0,0,0.06)"};
  const cardAccent={height:3,background:"linear-gradient(90deg,#1e3a8a,#1d4ed8)",borderRadius:"12px 12px 0 0",margin:"-1.8rem -1.8rem 1.5rem -1.8rem"};
  const inp={width:"100%",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,color:"#1e293b",fontSize:"0.95rem",padding:"0.65rem 0.9rem 0.65rem 1.9rem",outline:"none"};
  const sel={...inp,paddingLeft:"0.9rem"};
  const lbl={display:"block",fontSize:"0.78rem",fontWeight:600,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:"0.4rem"};
  const btn={width:"100%",padding:"0.85rem",background:"#dc2626",color:"#fff",border:"none",borderRadius:10,fontSize:"1rem",fontWeight:600,cursor:"pointer",marginTop:"0.5rem"};
  const radioActive={background:"rgba(30,58,138,0.08)",borderColor:"#1d4ed8",color:"#1d4ed8",fontWeight:600};
  const radioBase={display:"block",textAlign:"center",padding:"0.5rem 0.4rem",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,cursor:"pointer",color:"#64748b",flex:1,fontSize:"0.85rem"};
  const bc=["#1d4ed8","#2563eb","#3b82f6","#60a5fa","#dc2626","#ef4444","#f87171"];
  return(
    <div style={{background:"#f8fafc",color:"#1e293b",minHeight:"100vh",paddingBottom:"4rem"}}>
      <div style={{background:"linear-gradient(135deg,#1e3a8a 0%,#1e40af 60%,#1d4ed8 100%)",color:"#fff",padding:"1.5rem 1rem",marginBottom:"2rem"}}>
        <div style={{maxWidth:950,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:"1rem"}}>
          <div><h1 style={{fontSize:"1.8rem",fontWeight:700,margin:0}}>AI Tax Calculator</h1><p style={{color:"#bfdbfe",margin:"4px 0 0",fontSize:"0.875rem"}}>MetaVix Solutions · Federal &amp; State Tax Estimate — United States</p></div>
          <button onClick={onBack} style={{display:"inline-flex",alignItems:"center",gap:"0.5rem",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,padding:"0.55rem 1.2rem",fontSize:"0.875rem",fontWeight:600,cursor:"pointer"}}>← Back to AI Tools</button>
        </div>
      </div>
      <div style={{maxWidth:950,margin:"0 auto",padding:"0 1rem",display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1.5rem"}}>
        <div style={card}><div style={cardAccent}/><h3 style={{fontSize:"1.05rem",fontWeight:700,marginBottom:"1.5rem",color:"#1e3a8a"}}>💰 Income Details</h3>
          <div style={{marginBottom:"1.1rem"}}><label style={lbl}>Filing Status</label><div style={{display:"flex",gap:"0.5rem",flexWrap:"wrap"}}>{[["single","Single"],["mfj","Married Joint"],["mfs","Married Sep."],["hoh","Head of HH"]].map(([val,text])=><button key={val} onClick={()=>setFiling(val)} style={{...radioBase,...(filing===val?radioActive:{})}}>{text}</button>)}</div></div>
          {[["income","Gross Annual Income",75000],["otherIncome","Additional Income",0]].map(([key,text,ph])=><div key={key} style={{marginBottom:"1.1rem"}}><label style={lbl}>{text}</label><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#94a3b8"}}>$</span><input type="number" defaultValue={ph} onChange={e=>set(key,e.target.value)} style={inp}/></div></div>)}
          <div style={{marginBottom:"1.1rem"}}><label style={lbl}>State</label><select onChange={e=>setForm(f=>({...f,state:e.target.value}))} style={sel}><option value="0">No State Tax</option><option value="AL">Alabama (5%)</option><option value="AK">Alaska (0%)</option><option value="AZ">Arizona (2.5%)</option><option value="AR">Arkansas (3.9%)</option><option value="CA">California (up to 13.3%)</option><option value="CO">Colorado (4.4%)</option><option value="CT">Connecticut (3–6.99%)</option><option value="DE">Delaware (2.2–6.6%)</option><option value="FL">Florida (0%)</option><option value="GA">Georgia (5.49%)</option><option value="HI">Hawaii (1.4–11%)</option><option value="ID">Idaho (5.8%)</option><option value="IL">Illinois (4.95%)</option><option value="IN">Indiana (3%)</option><option value="IA">Iowa (3.8–5.7%)</option><option value="KS">Kansas (3.1–5.7%)</option><option value="KY">Kentucky (4%)</option><option value="LA">Louisiana (1.85–4.25%)</option><option value="ME">Maine (5.8–7.15%)</option><option value="MD">Maryland (2–5.75%)</option><option value="MA">Massachusetts (5%)</option><option value="MI">Michigan (4.25%)</option><option value="MN">Minnesota (5.35–9.85%)</option><option value="MS">Mississippi (4.7%)</option><option value="MO">Missouri (2–4.7%)</option><option value="MT">Montana (4.7–5.9%)</option><option value="NE">Nebraska (2.46–6.64%)</option><option value="NV">Nevada (0%)</option><option value="NH">New Hampshire (0%)</option><option value="NJ">New Jersey (1.4–10.75%)</option><option value="NM">New Mexico (1.7–5.9%)</option><option value="NY">New York (4–10.9%)</option><option value="NC">North Carolina (4.5%)</option><option value="ND">North Dakota (1.1–2.5%)</option><option value="OH">Ohio (2.765–3.99%)</option><option value="OK">Oklahoma (0.5–4.75%)</option><option value="OR">Oregon (4.75–9.9%)</option><option value="PA">Pennsylvania (3.07%)</option><option value="RI">Rhode Island (3.75–5.99%)</option><option value="SC">South Carolina (6.2%)</option><option value="SD">South Dakota (0%)</option><option value="TN">Tennessee (0%)</option><option value="TX">Texas (0%)</option><option value="UT">Utah (4.65%)</option><option value="VT">Vermont (3.35–8.75%)</option><option value="VA">Virginia (2–5.75%)</option><option value="WA">Washington (0%)</option><option value="WV">West Virginia (3–6.5%)</option><option value="WI">Wisconsin (3.54–7.65%)</option><option value="WY">Wyoming (0%)</option></select></div>
        </div>
        <div style={card}><div style={cardAccent}/><h3 style={{fontSize:"1.05rem",fontWeight:700,marginBottom:"1.5rem",color:"#1e3a8a"}}>📋 Deductions &amp; Credits</h3>
          <div style={{marginBottom:"1.1rem"}}><label style={lbl}>Deduction Type</label><div style={{display:"flex",gap:"0.5rem"}}>{[["standard","Standard"],["itemized","Itemized"]].map(([val,text])=><button key={val} onClick={()=>setDeductionType(val)} style={{...radioBase,...(deductionType===val?radioActive:{})}}>{text}</button>)}</div></div>
          {deductionType==="itemized"&&<div style={{marginBottom:"1.1rem"}}><label style={lbl}>Itemized Deduction Amount</label><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#94a3b8"}}>$</span><input type="number" defaultValue={0} onChange={e=>set("itemized",e.target.value)} style={inp}/></div></div>}
          {[["retirement","401(k) / Traditional IRA Contributions"],["hsa","HSA Contributions"],["credits","Tax Credits (Child Tax Credit, Education, etc.)"],["pretax","Pre-Tax Benefits (Health Ins., FSA, etc.)"]].map(([key,text])=><div key={key} style={{marginBottom:"1.1rem"}}><label style={lbl}>{text}</label><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#94a3b8"}}>$</span><input type="number" defaultValue={0} onChange={e=>set(key,e.target.value)} style={inp}/></div></div>)}
          <button onClick={calculate} style={btn}>Calculate My Taxes →</button>
        </div>
        <div style={{...card,gridColumn:"1 / -1"}}><div style={cardAccent}/><h3 style={{fontSize:"1.05rem",fontWeight:700,marginBottom:"0.75rem",color:"#1e3a8a"}}>🧾 Tax Already Withheld (Year-to-Date)</h3><p style={{color:"#64748b",fontSize:"0.85rem",marginBottom:"1.2rem"}}>Enter the taxes already withheld from your paychecks so far this year.</p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:"1rem"}}>{[["wFederal","Federal Tax Withheld"],["wState","State Tax Withheld"],["wSS","Social Security Withheld"],["wMedicare","Medicare Withheld"]].map(([key,text])=><div key={key}><label style={lbl}>{text}</label><div style={{position:"relative"}}><span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"#94a3b8"}}>$</span><input type="number" defaultValue={0} onChange={e=>set(key,e.target.value)} style={inp}/></div></div>)}</div>
          <div style={{marginTop:"1rem",padding:"0.75rem 1rem",background:"rgba(30,58,138,0.05)",border:"1px solid rgba(30,58,138,0.15)",borderRadius:8,fontSize:"0.83rem",color:"#64748b"}}>💡 Find these on your pay stubs (YTD columns) or most recent W-2 form.</div>
        </div>
        {result&&<div style={{...card,gridColumn:"1 / -1",borderTop:"3px solid #1d4ed8"}}>
          <h3 style={{fontSize:"1.05rem",fontWeight:700,marginBottom:"1.5rem",color:"#1e3a8a"}}>📊 Tax Summary</h3>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:"1rem",marginBottom:"2rem"}}>{[{label:"Est. Take-Home",value:fmt(result.takeHome),color:"#16a34a"},{label:"Total Tax",value:fmt(result.totalTax),color:"#dc2626"},{label:"Effective Rate",value:`${result.effectiveRate.toFixed(1)}%`,color:"#d97706"},{label:"Marginal Rate",value:`${result.marginalRate.toFixed(0)}%`,color:"#1d4ed8"}].map(item=><div key={item.label} style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:10,padding:"1rem",textAlign:"center"}}><span style={{fontSize:"0.72rem",color:"#64748b",textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:"0.4rem",fontWeight:600}}>{item.label}</span><div style={{fontSize:"1.35rem",fontWeight:700,color:item.color}}>{item.value}</div></div>)}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1.5rem"}}>
            <div><div style={{fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"0.08em",color:"#64748b",marginBottom:"0.75rem",fontWeight:600}}>Federal Tax Breakdown</div>{[["Gross Income",fmt(result.grossIncome),null],["Pre-Tax Adjustments",`− ${fmt(result.adjustments)}`,null],["Adjusted Gross Income",fmt(result.agi),null],[`${result.deductionType==="itemized"?"Itemized":"Standard"} Deduction`,`− ${fmt(result.deduction)}`,null],["Taxable Income",fmt(result.taxableIncome),null],["Federal Income Tax",fmt(result.fedTax),"#dc2626"],["Social Security Tax",fmt(result.ssTax),"#dc2626"],["Medicare Tax",fmt(result.medicareTax+result.addlMedicare),"#dc2626"],["State Income Tax",fmt(result.stateTax),"#dc2626"],["Net Take-Home Pay",fmt(result.takeHome),"#16a34a"]].map(([label,value,color])=><div key={label} style={{display:"flex",justifyContent:"space-between",padding:"0.45rem 0",fontSize:"0.875rem",borderTop:label==="Net Take-Home Pay"?"1px solid #e2e8f0":"none"}}><span style={{color:"#64748b"}}>{label}</span><span style={{fontWeight:600,color:color||"#1e293b"}}>{value}</span></div>)}</div>
            <div><div style={{fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"0.08em",color:"#64748b",marginBottom:"0.75rem",fontWeight:600}}>Federal Tax Brackets</div><table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.875rem"}}><thead><tr>{["Rate","Taxable Income","Tax"].map(h=><th key={h} style={{textAlign:"left",padding:"0.5rem 0.75rem",fontSize:"0.72rem",textTransform:"uppercase",color:"#64748b",borderBottom:"1px solid #e2e8f0"}}>{h}</th>)}</tr></thead><tbody>{result.bracketRows.map((r,i)=><tr key={i} style={{borderBottom:"1px solid #f1f5f9"}}><td style={{padding:"0.65rem 0.75rem"}}><span style={{background:`${bc[i]}18`,color:bc[i],padding:"0.15rem 0.6rem",borderRadius:20,fontSize:"0.78rem",fontWeight:700}}>{(r.rate*100).toFixed(0)}%</span></td><td style={{padding:"0.65rem 0.75rem",color:"#64748b",fontSize:"0.82rem"}}>{fmt(r.portion)}</td><td style={{padding:"0.65rem 0.75rem",color:bc[i],fontWeight:600}}>{fmt(r.tax)}</td></tr>)}</tbody></table></div>
          </div>
          <div style={{height:1,background:"#e2e8f0",margin:"1.5rem 0"}}/>
          <div style={{fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"0.08em",color:"#64748b",marginBottom:"0.75rem",fontWeight:600}}>Effective Tax Rate by Component</div>
          {[{name:"Federal Income Tax",amount:result.fedTax,color:"#1d4ed8"},{name:"Social Security",amount:result.ssTax,color:"#7c3aed"},{name:"Medicare",amount:result.medicareTax+result.addlMedicare,color:"#16a34a"},{name:"State Income Tax",amount:result.stateTax,color:"#d97706"}].map(c=>{const pct=result.grossIncome>0?(c.amount/result.grossIncome*100):0;return(<div key={c.name} style={{marginBottom:"0.85rem"}}><div style={{display:"flex",justifyContent:"space-between",fontSize:"0.875rem",marginBottom:"0.3rem"}}><span style={{color:"#64748b"}}>{c.name}</span><span style={{fontWeight:600,color:"#1e293b"}}>{fmt(c.amount)} <span style={{color:"#94a3b8",fontSize:"0.78rem"}}>({pct.toFixed(1)}%)</span></span></div><div style={{height:6,background:"#f1f5f9",borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.min(pct*3,100)}%`,background:c.color,borderRadius:3,transition:"width 0.6s ease"}}/></div></div>);})}
          <div style={{height:1,background:"#e2e8f0",margin:"1.5rem 0"}}/>
          <div style={{fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"0.08em",color:"#64748b",marginBottom:"0.75rem",fontWeight:600}}>Tax Withheld vs. Owed — Refund / Balance Due</div>
          <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.875rem",marginBottom:"1rem"}}><thead><tr>{["Tax Component","Est. Tax Owed","Withheld (YTD)","Difference"].map(h=><th key={h} style={{textAlign:"left",padding:"0.5rem 0.75rem",fontSize:"0.72rem",textTransform:"uppercase",color:"#64748b",borderBottom:"1px solid #e2e8f0"}}>{h}</th>)}</tr></thead><tbody>{[["Federal Income Tax",result.fedTax,result.wFederal],["State Income Tax",result.stateTax,result.wState],["Social Security Tax",result.ssTax,result.wSS],["Medicare Tax",result.medicareTax+result.addlMedicare,result.wMedicare]].map(([label,owed,withheld])=>{const diff=withheld-owed;return(<tr key={label} style={{borderBottom:"1px solid #f1f5f9"}}><td style={{padding:"0.65rem 0.75rem",fontWeight:600,color:"#1e293b"}}>{label}</td><td style={{padding:"0.65rem 0.75rem",color:"#dc2626"}}>{fmt(owed)}</td><td style={{padding:"0.65rem 0.75rem",color:"#1d4ed8"}}>{fmt(withheld)}</td><td style={{padding:"0.65rem 0.75rem",color:diff===0?"#94a3b8":diff>0?"#16a34a":"#dc2626",fontWeight:700}}>{diff===0?"Balanced":diff>0?`+ ${fmt(diff)} ↑ Refund`:`− ${fmt(Math.abs(diff))} ↓ Owed`}</td></tr>);})}<tr style={{borderTop:"2px solid #e2e8f0"}}><td style={{padding:"0.65rem 0.75rem",fontWeight:700,color:"#1e293b"}}>Total</td><td style={{padding:"0.65rem 0.75rem",color:"#dc2626",fontWeight:700}}>{fmt(result.totalTax)}</td><td style={{padding:"0.65rem 0.75rem",color:"#1d4ed8",fontWeight:700}}>{fmt(result.totalWithheld)}</td><td style={{padding:"0.65rem 0.75rem",fontWeight:700,color:result.netDiff>=0?"#16a34a":"#dc2626"}}>{result.netDiff===0?"✔ Balanced":result.netDiff>0?`🎉 Est. Refund: ${fmt(result.netDiff)}`:`⚠️ Est. Owed: ${fmt(Math.abs(result.netDiff))}`}</td></tr></tbody></table></div>
        </div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FINANCE ADVISOR v2.0 — INVESTMENT EXAMPLES DATABASE
// ─────────────────────────────────────────────────────────────────────────────

const INVESTMENT_EXAMPLES = {
  cash: {
    conservative: [
      {name:"High-Yield Savings Account",ticker:"HYSA",return:"4.5–5.1%",risk:"None",why:"FDIC insured and instantly accessible. The safest place for your emergency fund — earns real interest while you sleep."},
      {name:"Money Market Fund (Vanguard)",ticker:"VMFXX",return:"5.0–5.2%",risk:"Very Low",why:"Liquid, safe, and better-yielding than a regular bank account. Ideal for cash you might need within 30–90 days."},
      {name:"3-Month US Treasury Bill",ticker:"T-Bill",return:"5.0–5.3%",risk:"None",why:"Backed by the US government — literally the safest investment on earth. Park short-term cash here."},
      {name:"Short-Term CD (3–6 months)",ticker:"CD",return:"4.8–5.1%",risk:"None",why:"Lock in today's rates for a fixed period. Good if you won't need the cash for a few months."},
      {name:"Cash Management Account",ticker:"CMA",return:"4.5–5.0%",risk:"Very Low",why:"Offered by Fidelity or Schwab — combines checking convenience with savings-account interest rates."},
    ],
    growth: [
      {name:"High-Yield Savings Account",ticker:"HYSA",return:"4.5–5.1%",risk:"None",why:"Keep 1–2 months of expenses here for immediate access before deploying capital into investments."},
      {name:"Fidelity Money Market Fund",ticker:"SPAXX",return:"5.0–5.2%",risk:"Very Low",why:"Use this as a staging area — park cash here while you decide where and when to invest it."},
      {name:"3-Month US Treasury Bill",ticker:"T-Bill",return:"5.0–5.3%",risk:"None",why:"Roll quarterly for the best short-term government rates with zero credit risk."},
      {name:"Ultra-Short Bond ETF",ticker:"ICSH",return:"5.1–5.4%",risk:"Very Low",why:"iShares Ultra Short-Term Bond ETF — slightly more return than pure cash with minimal price volatility."},
      {name:"Cash Management Account",ticker:"CMA",return:"4.5–5.0%",risk:"Very Low",why:"Consolidate liquid cash in one place with competitive rates at major brokerages."},
    ],
  },
  fixed_income: {
    conservative: [
      {name:"Vanguard Total Bond Market ETF",ticker:"BND",return:"4.5–5.5%",risk:"Low",why:"Covers the entire US bond market — treasuries, corporate, municipal. This is the backbone of a conservative portfolio."},
      {name:"iShares US Treasury Bond ETF",ticker:"GOVT",return:"4.2–5.0%",risk:"Very Low",why:"Pure US government bonds. Maximum safety, zero credit risk. Your most defensive holding."},
      {name:"Vanguard Short-Term Bond ETF",ticker:"BSV",return:"4.8–5.3%",risk:"Low",why:"Less interest rate sensitivity than long-term bonds. Ideal when rates are elevated or uncertain."},
      {name:"iShares TIPS Bond ETF",ticker:"TIP",return:"3.5–4.5% + inflation",risk:"Low",why:"Treasury Inflation-Protected Securities — adjusts automatically with inflation. Never lose purchasing power."},
      {name:"Fidelity US Bond Index Fund",ticker:"FXNAX",return:"4.5–5.2%",risk:"Low",why:"Zero expense ratio. Tracks the Bloomberg US Aggregate Bond Index. Simple, cheap, and effective."},
    ],
    growth: [
      {name:"Vanguard Total Bond Market ETF",ticker:"BND",return:"4.5–5.5%",risk:"Low",why:"Your stability ballast. When equities fall 30%, bonds cushion the blow. This is why you hold them."},
      {name:"iShares Core US Aggregate Bond ETF",ticker:"AGG",return:"4.5–5.2%",risk:"Low",why:"One of the world's largest bond ETFs. Broad diversification across US fixed income in one ticker."},
      {name:"Vanguard Intermediate Treasury ETF",ticker:"VGIT",return:"4.3–5.0%",risk:"Low",why:"Mid-duration US treasuries — moderate interest rate sensitivity with strong government-backed credit quality."},
      {name:"iShares TIPS Bond ETF",ticker:"TIP",return:"3.5–4.5% + inflation",risk:"Low",why:"As a growth investor, inflation is your silent enemy. TIPS ensures your bond allocation fights back."},
      {name:"Schwab US Aggregate Bond ETF",ticker:"SCHZ",return:"4.5–5.1%",risk:"Low",why:"Ultra-low cost at 0.03% expense ratio. Every basis point saved in fees compounds into real money over decades."},
    ],
  },
  equity: {
    conservative: [
      {name:"Vanguard Dividend Appreciation ETF",ticker:"VIG",return:"7–10%",risk:"Medium-Low",why:"Companies with 10+ consecutive years of growing dividends. Lower volatility — perfect for equity exposure without wild swings."},
      {name:"Schwab US Dividend Equity ETF",ticker:"SCHD",return:"8–11%",risk:"Medium-Low",why:"High dividend yield combined with a quality screen. You collect quarterly income while the portfolio grows."},
      {name:"Vanguard S&P 500 ETF",ticker:"VOO",return:"8–12%",risk:"Medium",why:"The gold standard of investing. 500 largest US companies, 0.03% cost, decades of proven compounding."},
      {name:"iShares MSCI World ETF",ticker:"URTH",return:"7–11%",risk:"Medium",why:"Diversifies beyond the US into 23 developed markets. Reduces single-country risk while maintaining quality."},
      {name:"Vanguard Balanced Index Fund",ticker:"VBIAX",return:"6–9%",risk:"Medium-Low",why:"60% stocks / 40% bonds in one fund. Ultimate simplicity covering your equity and fixed income allocation."},
    ],
    growth: [
      {name:"Vanguard S&P 500 ETF",ticker:"VOO",return:"9–13%",risk:"Medium",why:"Your core holding. Broad US market, lowest cost in the industry, best long-term track record."},
      {name:"Invesco QQQ Trust (Nasdaq 100)",ticker:"QQQ",return:"11–16%",risk:"Medium-High",why:"Top 100 Nasdaq companies — Apple, Microsoft, Nvidia, Google. High growth exposure, higher volatility."},
      {name:"Vanguard Total World Stock ETF",ticker:"VT",return:"8–12%",risk:"Medium",why:"Own every publicly traded company on earth in one ETF. 9,000+ companies across 50 countries."},
      {name:"iShares MSCI Emerging Markets ETF",ticker:"EEM",return:"8–14%",risk:"High",why:"India, China, Brazil, Indonesia. Higher risk but powerful long-term growth for patient investors."},
      {name:"Vanguard Growth ETF",ticker:"VUG",return:"10–15%",risk:"Medium-High",why:"US large-cap growth companies. If you believe innovation will drive returns this decade, this is your vehicle."},
    ],
    aggressive: [
      {name:"Invesco QQQ Trust",ticker:"QQQ",return:"11–16%",risk:"High",why:"Concentrated tech and growth exposure. This is where aggressive returns come from. Ride the AI wave."},
      {name:"Vanguard S&P 500 ETF",ticker:"VOO",return:"9–13%",risk:"Medium",why:"Even the most aggressive portfolios need an anchor. VOO grounds you in 500 proven businesses."},
      {name:"ARK Innovation ETF",ticker:"ARKK",return:"Variable / High",risk:"Very High",why:"Disruptive innovation — genomics, robotics, AI, fintech. Expect 40–60% drawdowns. Long-term only."},
      {name:"iShares MSCI Emerging Markets ETF",ticker:"EEM",return:"8–14%",risk:"High",why:"Geographic diversification into the fastest-growing economies. India's middle class adds a massive tailwind."},
      {name:"Vanguard Small-Cap Growth ETF",ticker:"VBK",return:"10–16%",risk:"High",why:"Small companies grow faster than large ones historically. Higher volatility but compounding accelerates."},
    ],
  },
  gold: [
    {name:"SPDR Gold Shares",ticker:"GLD",return:"6–9% long-term",risk:"Medium",why:"The most liquid gold ETF globally. When inflation spikes or markets crash, gold typically holds value."},
    {name:"iShares Gold Trust",ticker:"IAU",return:"6–9% long-term",risk:"Medium",why:"Same exposure as GLD but 0.15% cheaper annually. For long holders, lower fees compound into real savings."},
    {name:"VanEck Gold Miners ETF",ticker:"GDX",return:"8–14% (leveraged to gold)",risk:"High",why:"Mining stocks amplify gold's moves — gold up 10% often means miners up 20–30%. Higher risk, higher return."},
    {name:"Aberdeen Physical Gold ETF",ticker:"SGOL",return:"6–9% long-term",risk:"Medium",why:"Physical gold stored in Swiss vaults. Strong geopolitical hedge for financial system stress scenarios."},
    {name:"Invesco DB Commodity Index",ticker:"DBC",return:"5–10%",risk:"Medium-High",why:"Broader commodity basket covering gold, oil, and agriculture. Fuller inflation protection."},
  ],
};

function getInvestmentExamples(assetClass, profile) {
  const p = profile.toLowerCase();
  if (assetClass === "Cash") return (p === "conservative" || p === "moderate") ? INVESTMENT_EXAMPLES.cash.conservative : INVESTMENT_EXAMPLES.cash.growth;
  if (assetClass === "Fixed income / Debt") return (p === "conservative" || p === "moderate") ? INVESTMENT_EXAMPLES.fixed_income.conservative : INVESTMENT_EXAMPLES.fixed_income.growth;
  if (assetClass === "Equity") {
    if (p === "aggressive") return INVESTMENT_EXAMPLES.equity.aggressive;
    if (p === "growth-oriented") return INVESTMENT_EXAMPLES.equity.growth;
    return INVESTMENT_EXAMPLES.equity.conservative;
  }
  if (assetClass === "Gold / Alternatives") return INVESTMENT_EXAMPLES.gold;
  return [];
}

// ─────────────────────────────────────────────────────────────────────────────
// MASTER QUESTION BANK — Single source, layer-tagged
//
// Each question carries a `layers` array: [1], [1,2], or [1,2,3]
// The question flow for each config filters to only show questions whose
// layers array is a subset of the config's active layers.
//
//  Config A — Personal    : layers [1]       → 29 questions
//  Config B — Premium     : layers [1,2]     → 29 + 5 behavioral questions
//  Config C — HNI/Elite   : layers [1,2,3]   → 29 + 5 behavioral + 6 capital structure
//  Config D — Retirement  : uses RETIREMENT_QUESTIONS (specialist vertical)
//
// Phase labels:
//  Phase 1 — Basic Profile       (L1)
//  Phase 2 — Income & Expenses   (L1)
//  Phase 3 — Assets              (L1)
//  Phase 4 — Liabilities         (L1)
//  Phase 5 — Protection          (L1)
//  Phase 6 — Financial Goals     (L1)
//  Phase 7 — Risk Assessment     (L1)
//  Phase 8 — Behavioral AI       (L2, added for Config B+)
//  Phase 9 — Capital Structure   (L3, added for Config C only)
// ─────────────────────────────────────────────────────────────────────────────

const MASTER_QUESTIONS = [
  // ── LAYER 1: Core (all configs A/B/C) ─────────────────────────────────────
  // Phase 1 — Basic Profile
  {id:"age",           layers:[1],     phaseLabel:"Phase 1 — Basic Profile",     text:"How old are you?",                                                    type:"number",  hint:"Enter your age in years.",                                          min:18,max:90},
  {id:"country",       layers:[1],     phaseLabel:"Phase 1 — Basic Profile",     text:"Which country do you currently live in?",                             type:"text",    hint:"E.g. United States, India, UK, Australia…"},
  {id:"marital",       layers:[1],     phaseLabel:"Phase 1 — Basic Profile",     text:"What is your marital status?",                                        type:"options", options:["Single","Married","Divorced","Widowed"]},
  {id:"dependents",    layers:[1],     phaseLabel:"Phase 1 — Basic Profile",     text:"How many dependents do you have?",                                    type:"number",  hint:"Include children or anyone financially relying on you.",            min:0,max:20},
  {id:"employment",    layers:[1],     phaseLabel:"Phase 1 — Basic Profile",     text:"What is your employment type?",                                       type:"options", options:["Salaried (full-time)","Self-employed","Freelance / Contract","Business owner","Retired","Unemployed"]},
  {id:"income_stability",layers:[1],   phaseLabel:"Phase 1 — Basic Profile",     text:"How stable is your income?",                                          type:"options", options:["Stable","Somewhat variable","Highly variable"]},
  // Phase 2 — Income & Expenses
  {id:"income",        layers:[1],     phaseLabel:"Phase 2 — Income & Expenses", text:"What is your monthly take-home income?",                              type:"number",  hint:"Enter the amount — you will specify currency next.",               min:0},
  {id:"currency",      layers:[1],     phaseLabel:"Phase 2 — Income & Expenses", text:"What currency is your income in?",                                    type:"text",    hint:"E.g. USD, INR, GBP, EUR, AED…"},
  {id:"expenses",      layers:[1],     phaseLabel:"Phase 2 — Income & Expenses", text:"What are your total monthly expenses?",                               type:"number",  hint:"Include rent, food, utilities, subscriptions, etc.",               min:0},
  {id:"savings_monthly",layers:[1],    phaseLabel:"Phase 2 — Income & Expenses", text:"How much do you save each month?",                                    type:"number",  hint:"Your current monthly savings amount.",                              min:0},
  // Phase 3 — Assets
  {id:"cash_savings",         layers:[1], phaseLabel:"Phase 3 — Assets",         text:"What is your total cash savings / emergency fund?",                   type:"number",  hint:"Savings accounts, fixed deposits, etc.",                            min:0},
  {id:"existing_investments", layers:[1], phaseLabel:"Phase 3 — Assets",         text:"What is the approximate total value of your existing investments?",   type:"number",  hint:"Stocks, mutual funds, bonds, crypto, etc. Enter 0 if none.",       min:0},
  {id:"retirement_accounts",  layers:[1], phaseLabel:"Phase 3 — Assets",         text:"What is the current value of your retirement accounts?",              type:"number",  hint:"401k, IRA, EPF, pension, or equivalent. Enter 0 if none.",         min:0},
  {id:"real_estate",           layers:[1], phaseLabel:"Phase 3 — Assets",        text:"What is the approximate value of real estate you own?",               type:"number",  hint:"Primary home, rental property, etc. Enter 0 if none.",             min:0},
  // Phase 4 — Liabilities
  {id:"has_loans",  layers:[1], phaseLabel:"Phase 4 — Liabilities",              text:"Do you currently have any outstanding loans?",                        type:"options", options:["Yes","No"]},
  {id:"loan_amount",layers:[1], phaseLabel:"Phase 4 — Liabilities",              text:"What is the total outstanding loan amount?",                          type:"number",  hint:"Sum of all loans — home, car, personal, student, etc.",            min:0,  conditional:d=>d.has_loans==="Yes"},
  {id:"loan_rate",  layers:[1], phaseLabel:"Phase 4 — Liabilities",              text:"What is the average interest rate on your loans?",                    type:"number",  hint:"Enter as a percentage, e.g. 7.5 for 7.5%",                         min:0,max:60,conditional:d=>d.has_loans==="Yes"},
  {id:"loan_emi",   layers:[1], phaseLabel:"Phase 4 — Liabilities",              text:"What is your total monthly loan EMI / repayment amount?",             type:"number",  hint:"Sum of all monthly loan repayments.",                               min:0,  conditional:d=>d.has_loans==="Yes"},
  // Phase 5 — Protection & Access
  {id:"health_insurance",  layers:[1], phaseLabel:"Phase 5 — Protection & Access", text:"Do you have health insurance?",                                     type:"options", options:["Yes","No"]},
  {id:"life_insurance",    layers:[1], phaseLabel:"Phase 5 — Protection & Access", text:"Do you have life insurance?",                                       type:"options", options:["Yes","No"]},
  {id:"retirement_access", layers:[1], phaseLabel:"Phase 5 — Protection & Access", text:"Do you have access to a tax-advantaged retirement account (401k, IRA, PPF, pension, or equivalent)?", type:"options", options:["Yes","No"]},
  // Phase 6 — Financial Goals
  {id:"goal_primary",  layers:[1], phaseLabel:"Phase 6 — Financial Goals",       text:"What is your most important financial goal right now?",               type:"text",    hint:"E.g. buy a home, retire early, build emergency fund, pay off debt…"},
  {id:"goal_timeline", layers:[1], phaseLabel:"Phase 6 — Financial Goals",       text:"When do you want to achieve this goal?",                              type:"options", options:["Within 1 year","1–3 years","3–5 years","5–10 years","10+ years"]},
  {id:"goal_other",    layers:[1], phaseLabel:"Phase 6 — Financial Goals",       text:"Do you have any other major financial goals?",                        type:"text",    hint:"E.g. children's education, travel, business. Enter 'None' if not."},
  {id:"retirement_age",layers:[1], phaseLabel:"Phase 6 — Financial Goals",       text:"At what age do you plan to retire?",                                  type:"number",  hint:"Enter your target retirement age.",                                 min:40,max:80},
  // Phase 7 — Risk Assessment (core)
  {id:"market_drop",       layers:[1], phaseLabel:"Phase 7 — Risk Assessment",   text:"If your investments dropped 20% in value, what would you most likely do?",   type:"options", options:["Sell everything","Sell some and wait","Do nothing and wait","Invest more — buy the dip"]},
  {id:"return_preference", layers:[1], phaseLabel:"Phase 7 — Risk Assessment",   text:"Which investment style do you prefer?",                               type:"options", options:["Stable returns (low risk)","Balanced growth (moderate risk)","High growth with volatility (high risk)"]},
  {id:"equity_experience", layers:[1], phaseLabel:"Phase 7 — Risk Assessment",   text:"Have you invested in stocks or equity funds before?",                 type:"options", options:["Yes","No"]},
  {id:"market_comfort",    layers:[1], phaseLabel:"Phase 7 — Risk Assessment",   text:"How comfortable are you with day-to-day market fluctuations?",        type:"options", options:["Low — I get anxious","Medium — I can tolerate it","High — I am comfortable"]},

  // ── LAYER 2: Behavioral AI (Config B and C) ────────────────────────────────
  // Phase 8 — Behavioral Finance (deeper battery)
  {id:"past_decision_regret", layers:[1,2], phaseLabel:"Phase 8 — Behavioral AI",  text:"Thinking back to a past investment loss — how did you react?",      type:"options", options:["I panicked and sold","I sold some but regretted it","I held and recovered","I added to the position"]},
  {id:"return_vs_certainty",  layers:[1,2], phaseLabel:"Phase 8 — Behavioral AI",  text:"Would you prefer a guaranteed 6% return or a 70% chance of 12% with a 30% chance of −2%?",type:"options", options:["Guaranteed 6%","Take the 70/30 bet"]},
  {id:"check_portfolio_freq", layers:[1,2], phaseLabel:"Phase 8 — Behavioral AI",  text:"How often do you check your portfolio?",                             type:"options", options:["Multiple times daily","Daily","Weekly","Monthly or less"]},
  {id:"loss_vs_gain_reaction",layers:[1,2], phaseLabel:"Phase 8 — Behavioral AI",  text:"Which scenario bothers you more?",                                   type:"options", options:["Losing 10% when the market fell 5%","Missing a 15% gain when the market rose 20%"]},
  {id:"risk_preference",      layers:[1,2], phaseLabel:"Phase 8 — Behavioral AI",  text:"Describe your overall risk preference:",                             type:"options", options:["Capital preservation","Moderate growth","Strong growth","Maximum growth / opportunistic"]},

  // ── LAYER 3: Capital Structure (Config C only) ─────────────────────────────
  // Phase 9 — Capital Structure
  {id:"business_owner",         layers:[1,2,3], phaseLabel:"Phase 9 — Capital Structure", text:"Do you own a business?",                                      type:"options", options:["Yes — majority owner (>50%)","Yes — minority stake","No"]},
  {id:"net_worth_in_one_asset",  layers:[1,2,3], phaseLabel:"Phase 9 — Capital Structure", text:"What percentage of your net worth is tied to a single asset (business, property, or stock)?", type:"number", hint:"Enter 0–100.", min:0,max:100},
  {id:"concentrated_stock",      layers:[1,2,3], phaseLabel:"Phase 9 — Capital Structure", text:"Do you hold concentrated stock positions (single stock >10% of investable assets)?", type:"options", options:["Yes","No"]},
  {id:"private_equity",          layers:[1,2,3], phaseLabel:"Phase 9 — Capital Structure", text:"Do you have private equity or alternative investments?",      type:"options", options:["Yes — significant (>15%)","Yes — some (<15%)","No"]},
  {id:"estate_structures",       layers:[1,2,3], phaseLabel:"Phase 9 — Capital Structure", text:"Do you have estate planning structures in place (trusts, wills, beneficiary reviews)?", type:"options", options:["Yes — fully structured","Partially in place","Not yet"]},
  {id:"philanthropic",           layers:[1,2,3], phaseLabel:"Phase 9 — Capital Structure", text:"Do you have philanthropic objectives?",                       type:"options", options:["Yes — active giving","Planning to","No"]},
];

// Helper: filter questions by active layers for a given config
// activeLayers: e.g. [1] for Config A, [1,2] for Config B, [1,2,3] for Config C
function getQuestionsForConfig(activeLayers) {
  return MASTER_QUESTIONS.filter(q =>
    q.layers.every(l => activeLayers.includes(l))
  );
}

// Convenience exports used by each advisor
const FA_QUESTIONS         = getQuestionsForConfig([1]);       // Config A
const PREMIUM_QUESTIONS    = getQuestionsForConfig([1,2]);     // Config B
const ELITE_QUESTIONS_ALL  = getQuestionsForConfig([1,2,3]);   // Config C

// ─────────────────────────────────────────────────────────────────────────────
// FINANCE ADVISOR v2.0 — DYNAMIC BRANCHING INTELLIGENCE ENGINE
// ─────────────────────────────────────────────────────────────────────────────

// ① Financial Complexity Tier Classification
function classifyTier(income, netWorth, investments, loanAmt, realEstate) {
  const annualIncome = income * 12;
  const hasMultipleAssets = (investments > 0 ? 1 : 0) + (realEstate > 0 ? 1 : 0) + (loanAmt > 0 ? 1 : 0);
  if (annualIncome > 150000 || netWorth > annualIncome * 5 || hasMultipleAssets >= 3) return 3;
  if (annualIncome > 60000 || netWorth > annualIncome * 2 || hasMultipleAssets >= 2) return 2;
  return 1;
}

// ② Behavioral Bias Detection
function detectBiases(data) {
  const biases = [];
  // Loss aversion: says sell everything or sell some on 20% drop
  if (data.market_drop === "Sell everything" || data.market_drop === "Sell some and wait") {
    biases.push({
      type: "Loss Aversion",
      icon: "😰",
      color: "#dc2626",
      bg: "#fef2f2",
      border: "#fecaca",
      description: "You tend to feel losses more acutely than equivalent gains. This can cause panic-selling at market lows — the costliest investing mistake.",
      adjustment: "Equity allocation reduced by 5% to reduce emotional volatility exposure.",
      equityAdjust: -5,
    });
  }
  // Overconfidence: high growth preference + buy the dip + comfort high
  if (
    data.return_preference === "High growth with volatility (high risk)" &&
    data.market_drop === "Invest more — buy the dip" &&
    data.market_comfort === "High — I am comfortable"
  ) {
    biases.push({
      type: "Overconfidence",
      icon: "🚀",
      color: "#d97706",
      bg: "#fffbeb",
      border: "#fde68a",
      description: "Your answers suggest very high confidence in market navigation. Overconfidence can lead to concentration risk and under-diversification.",
      adjustment: "Portfolio diversification emphasized across geographies and asset classes.",
      equityAdjust: 0,
    });
  }
  // Recency bias: equity_experience yes but market_drop sell + stable preference
  if (
    data.equity_experience === "Yes" &&
    data.return_preference === "Stable returns (low risk)" &&
    (data.market_drop === "Sell everything" || data.market_drop === "Sell some and wait")
  ) {
    biases.push({
      type: "Recency Bias",
      icon: "📉",
      color: "#7c3aed",
      bg: "#f5f3ff",
      border: "#ddd6fe",
      description: "Despite investing experience, recent market events appear to be driving a more cautious stance. Past short-term volatility shouldn't dictate long-term strategy.",
      adjustment: "A structured rebalancing schedule has been added to counter timing-driven decisions.",
      equityAdjust: -3,
    });
  }
  return biases;
}

// ③ Conditional Branch Rules — returns override modifiers
function applyBranchRules(data, baseAlloc, emergencyMonths, loanRate, expReturnMidpoint, netWorth, income, age) {
  const rules = [];
  let equityDelta = 0;
  let fixedDelta = 0;
  let cashDelta = 0;
  let pauseInvesting = false;
  let debtFirst = false;
  let estatePositioning = false;

  // Rule A: Emergency fund < 3 months → pause investing
  if (emergencyMonths < 3) {
    pauseInvesting = true;
    rules.push({
      icon: "🚨",
      severity: "critical",
      title: "Investing Paused — Liquidity Priority",
      detail: `Your emergency fund covers only ${emergencyMonths.toFixed(1)} months. All investable surplus should first go to building 3–6 months of expenses in a High-Yield Savings Account. No allocation to equity or bonds until this is resolved.`,
    });
    cashDelta += 10; equityDelta -= 8; fixedDelta -= 2;
  }

  // Rule B: Debt rate > expected investment return → debt first
  if (loanRate > expReturnMidpoint && loanRate > 0) {
    debtFirst = true;
    rules.push({
      icon: "💳",
      severity: "high",
      title: "Debt Repayment Prioritized Over Investing",
      detail: `Your loan rate (${loanRate}%) exceeds the expected portfolio return (${expReturnMidpoint.toFixed(1)}%). Paying down debt is a guaranteed ${loanRate}% return. Direct surplus income to debt reduction before investing.`,
    });
    cashDelta += 5; equityDelta -= 5;
  }

  // Rule C: Age < 35 → increase growth tilt
  if (age < 35) {
    equityDelta += 5; fixedDelta -= 5;
    rules.push({
      icon: "🌱",
      severity: "info",
      title: "Youth Growth Tilt Applied",
      detail: "With decades of compounding ahead, equity allocation has been increased by 5%. Short-term volatility is a minor cost compared to the long-term compounding advantage you have.",
    });
  }

  // Rule D: Age > 55 → capital preservation bias
  if (age > 55) {
    equityDelta -= 8; fixedDelta += 8;
    rules.push({
      icon: "🛡️",
      severity: "info",
      title: "Capital Preservation Bias Applied",
      detail: "As you approach retirement, fixed income has been increased by 8% to protect accumulated wealth from late-stage market drawdowns. A major correction near retirement is the primary wealth risk to manage.",
    });
  }

  // Rule E: Net worth > 5× annual income → tax efficiency + estate
  if (income > 0 && netWorth > income * 12 * 5) {
    estatePositioning = true;
    rules.push({
      icon: "🏛️",
      severity: "info",
      title: "Tax Efficiency & Estate Positioning Unlocked",
      detail: "Your net worth exceeds 5× annual income. Priority actions: maximize tax-advantaged account contributions, consider tax-loss harvesting, review beneficiary designations, and explore estate planning with a fiduciary advisor.",
    });
  }

  return { rules, equityDelta, fixedDelta, cashDelta, pauseInvesting, debtFirst, estatePositioning };
}

// ④ Stress Test Calculator
function computeStressTest(alloc, netWorth, currency) {
  const equityPct = (alloc.find(a => a.cls === "Equity")?.pct || 0) / 100;
  const fixedPct = (alloc.find(a => a.cls === "Fixed income / Debt")?.pct || 0) / 100;
  const goldPct = (alloc.find(a => a.cls === "Gold / Alternatives")?.pct || 0) / 100;
  const cashPct = (alloc.find(a => a.cls === "Cash")?.pct || 0) / 100;

  // Assumptions: equity drops full correction amount, bonds slight rise, gold slight rise, cash stable
  const scenarios = [
    {
      label: "10% Market Correction",
      desc: "A mild pullback — happens roughly every 1–2 years on average.",
      equityImpact: -0.10,
      fixedImpact: +0.02,
      goldImpact: +0.03,
      cashImpact: 0,
      recoveryTime: "3–6 months",
      recoveryNote: "Historically, 10% corrections recover quickly. Staying invested is typically the optimal strategy.",
    },
    {
      label: "20% Bear Market",
      desc: "A significant bear market — happens every 3–5 years on average.",
      equityImpact: -0.20,
      fixedImpact: +0.04,
      goldImpact: +0.06,
      cashImpact: 0,
      recoveryTime: "12–24 months",
      recoveryNote: "Bear markets are the price of long-term returns. Portfolios with diversification recover materially faster than pure equity.",
    },
  ];

  return scenarios.map(s => {
    const portfolioImpactPct =
      equityPct * s.equityImpact +
      fixedPct * s.fixedImpact +
      goldPct * s.goldImpact +
      cashPct * s.cashImpact;
    const portfolioImpactAmt = netWorth > 0 ? netWorth * portfolioImpactPct : null;
    const displayImpact = (portfolioImpactPct * 100).toFixed(1);
    return { ...s, portfolioImpactPct, portfolioImpactAmt, displayImpact };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// MODULAR ADVISORY ENGINE — 5-Layer Architecture
//
// LAYER 1 │ Core Advisory Engine     — mandatory, always runs
// LAYER 2 │ Behavioral AI 3.0        — activated after behavioral phase
// LAYER 3 │ Elite / HNI Module       — activated by tier/complexity detection
// LAYER 4 │ Compliance Overlay       — always-on, appended last, never mutates logic
// LAYER 5 │ SaaS System Wrapper      — handled at application level (not in this file)
// ─────────────────────────────────────────────────────────────────────────────

// ── LAYER 1: Core Advisory Engine ────────────────────────────────────────────
// Responsibility: data parsing, financial health metrics, risk scoring,
// base profile/allocation mapping, strategy fields, and action plan.
// This layer runs unconditionally and produces a self-contained base result.

function layer1_coreEngine(data) {
  // Parse inputs
  const income      = parseFloat(data.income)            || 0;
  const expenses    = parseFloat(data.expenses)          || 0;
  const savings     = parseFloat(data.savings_monthly)   || 0;
  const cash        = parseFloat(data.cash_savings)      || 0;
  const investments = parseFloat(data.existing_investments) || 0;
  const retirement  = parseFloat(data.retirement_accounts)  || 0;
  const realEstate  = parseFloat(data.real_estate)       || 0;
  const loanAmt     = parseFloat(data.loan_amount)       || 0;
  const loanRate    = parseFloat(data.loan_rate)         || 0;
  const loanEMI     = parseFloat(data.loan_emi)          || 0;
  const age         = parseInt(data.age)                 || 35;
  const retireAge   = parseInt(data.retirement_age)      || 60;
  const currency    = (data.currency || "USD").toUpperCase();

  // Core financial health metrics
  const savingsRate     = income > 0 ? (savings / income) * 100 : 0;
  const monthlyExp      = expenses + loanEMI;
  const emergencyMonths = monthlyExp > 0 ? cash / monthlyExp : 0;
  const dti             = income > 0 ? (loanEMI / income) * 100 : 0;
  const netWorth        = (cash + investments + retirement + realEstate) - loanAmt;

  // Behavioral risk score (raw questionnaire answers → numeric score)
  let bScore = 0;
  if (data.market_drop === "Invest more — buy the dip") bScore += 20;
  else if (data.market_drop === "Do nothing and wait")  bScore += 14;
  else if (data.market_drop === "Sell some and wait")   bScore += 7;
  if (data.return_preference === "High growth with volatility (high risk)") bScore += 16;
  else if (data.return_preference === "Balanced growth (moderate risk)")    bScore += 10;
  else bScore += 4;
  if (data.equity_experience === "Yes")                       bScore += 8;
  if (data.market_comfort === "High — I am comfortable")      bScore += 6;
  else if (data.market_comfort === "Medium — I can tolerate it") bScore += 3;

  // Financial capacity score (balance sheet strength → numeric score)
  let fScore = 0;
  if (savingsRate >= 30) fScore += 14; else if (savingsRate >= 20) fScore += 10; else if (savingsRate >= 10) fScore += 6; else fScore += 2;
  if (emergencyMonths >= 6) fScore += 10; else if (emergencyMonths >= 3) fScore += 6; else fScore += 2;
  if (dti === 0) fScore += 10; else if (dti < 20) fScore += 7; else if (dti < 35) fScore += 3;
  if (netWorth > income * 24) fScore += 8; else if (netWorth > income * 12) fScore += 5; else if (netWorth > 0) fScore += 2;
  if (data.health_insurance === "Yes") fScore += 4;
  if (data.life_insurance    === "Yes") fScore += 4;

  const composite = bScore + fScore;

  // Risk profile → base allocation mapping
  let profile, profileDesc, alloc, expReturn, expRealReturn;
  if (composite >= 75) {
    profile = "Aggressive";
    profileDesc = "You have strong financial capacity and a high appetite for risk. Your portfolio is engineered for maximum long-term wealth accumulation. Expect significant short-term swings of 30–40% — that's the price of higher returns. Stay the course.";
    alloc = [{cls:"Cash",pct:5,color:"#64748b",reason:"Minimal cash drag — just enough for opportunities"},{cls:"Fixed income / Debt",pct:10,color:"#3b82f6",reason:"Thin safety net, mostly short-duration bonds"},{cls:"Equity",pct:75,color:"#16a34a",reason:"Primary wealth engine — broad + growth exposure"},{cls:"Gold / Alternatives",pct:10,color:"#d97706",reason:"Hedge against tail risks and currency debasement"}];
    expReturn = "10–14%"; expRealReturn = "7–11%";
  } else if (composite >= 55) {
    profile = "Growth-oriented";
    profileDesc = "You're comfortable with market volatility and have solid financial foundations. Your portfolio leans heavily toward growth while keeping a sensible buffer for stability.";
    alloc = [{cls:"Cash",pct:8,color:"#64748b",reason:"Adequate buffer — 1–2 months of expenses ready"},{cls:"Fixed income / Debt",pct:20,color:"#3b82f6",reason:"Ballast during equity downturns"},{cls:"Equity",pct:62,color:"#16a34a",reason:"Core growth engine — diversified across markets"},{cls:"Gold / Alternatives",pct:10,color:"#d97706",reason:"Inflation hedge and portfolio smoother"}];
    expReturn = "8–12%"; expRealReturn = "5–9%";
  } else if (composite >= 35) {
    profile = "Moderate";
    profileDesc = "You want your money to grow but you also value stability. This balanced portfolio gives you meaningful growth with enough cushion to avoid panic-selling during corrections.";
    alloc = [{cls:"Cash",pct:10,color:"#64748b",reason:"Peace of mind + opportunity reserve"},{cls:"Fixed income / Debt",pct:35,color:"#3b82f6",reason:"Strong stability backbone — reduces portfolio volatility"},{cls:"Equity",pct:45,color:"#16a34a",reason:"Growth engine, sized to your comfort level"},{cls:"Gold / Alternatives",pct:10,color:"#d97706",reason:"Safe haven — performs when stocks and bonds both fall"}];
    expReturn = "6–9%"; expRealReturn = "3–6%";
  } else {
    profile = "Conservative";
    profileDesc = "Capital protection is your top priority. This portfolio is designed to preserve what you've built while still outpacing inflation.";
    alloc = [{cls:"Cash",pct:15,color:"#64748b",reason:"High liquidity — always ready for emergencies or opportunities"},{cls:"Fixed income / Debt",pct:55,color:"#3b82f6",reason:"Core of portfolio — steady income, capital protection"},{cls:"Equity",pct:20,color:"#16a34a",reason:"Just enough growth to beat inflation over time"},{cls:"Gold / Alternatives",pct:10,color:"#d97706",reason:"Inflation shield — protects purchasing power long-term"}];
    expReturn = "4–7%"; expRealReturn = "1–4%";
  }

  // Strategy fields
  const approach      = savings > income * 0.25 ? "SIP + Lump sum hybrid" : "SIP (systematic monthly)";
  const volatility    = composite >= 75 ? "High" : composite >= 55 ? "Moderate–High" : composite >= 35 ? "Moderate" : "Low";
  const rebalance     = composite >= 55 ? "Semi-annually" : "Annually";
  const yearsToRetire = Math.max(retireAge - age, 1);
  const monthlySIP    = Math.round(savings * 0.8);

  // Core action plan
  const actions = [];
  if (emergencyMonths < 3) actions.push({icon:"🚨",title:"Build your emergency fund first",detail:`You currently have ${emergencyMonths.toFixed(1)} months of coverage — the target is 6 months (~${currency} ${Math.round(monthlyExp * 6).toLocaleString()}). Put this in a High-Yield Savings Account. Do not invest this money — it's your financial safety net.`});
  if (data.health_insurance === "No") actions.push({icon:"🏥",title:"Get health insurance immediately",detail:"A single medical emergency without coverage can erase years of savings. This is your most urgent financial vulnerability — fix it before investing a single dollar."});
  if (data.life_insurance === "No" && parseInt(data.dependents) > 0) actions.push({icon:"🛡️",title:"Get term life insurance",detail:`You have ${data.dependents} dependent(s) relying on your income. A 20-year term policy covering 10–12× your annual income is affordable and non-negotiable.`});
  if (loanRate > 10 && loanAmt > 0) actions.push({icon:"💳",title:"Aggressively pay down high-interest debt",detail:`Your loan at ${loanRate}% is costing more than your investments will likely earn. Paying this down is a guaranteed ${loanRate}% return.`});
  if (data.retirement_access === "Yes") actions.push({icon:"🏦",title:"Max out your tax-advantaged retirement accounts",detail:`You have ${yearsToRetire} years until retirement. Maximize 401k/IRA/PPF before any taxable investing — the tax savings compound dramatically.`});
  if (savingsRate < 10) actions.push({icon:"📈",title:"Increase your savings rate to at least 15%",detail:`You're currently saving ${savingsRate.toFixed(1)}% of income. Increasing to 15–20% compresses your financial independence timeline by years.`});
  actions.push({icon:"🔄",title:`Automate a monthly SIP of ${currency} ${monthlySIP.toLocaleString()}`,detail:"Set up an automatic monthly transfer into your investment account on payday. Time in the market beats timing the market — every single time."});
  actions.push({icon:"⚖️",title:`Rebalance your portfolio ${rebalance.toLowerCase()}`,detail:"Set a calendar reminder. If equities grew to 55% but your target is 45%, sell the overage and buy what's lagged. This automatically forces you to buy low and sell high."});

  return {
    // Parsed inputs (passed to later layers)
    income, expenses, savings, cash, investments, retirement, realEstate,
    loanAmt, loanRate, loanEMI, age, retireAge, currency,
    // Core metrics
    savingsRate: parseFloat(savingsRate.toFixed(1)),
    emergencyMonths: parseFloat(emergencyMonths.toFixed(1)),
    dti: parseFloat(dti.toFixed(1)),
    netWorth,
    // Scores
    bScore, fScore, composite,
    // Profile + allocation (may be adjusted by Layer 2/3)
    profile, profileDesc, alloc, expReturn, expRealReturn,
    // Strategy
    approach, volatility, rebalance, yearsToRetire, monthlySIP, actions,
  };
}

// ── LAYER 2: Behavioral AI 3.0 ───────────────────────────────────────────────
// Responsibility: bias detection, emotional stability weighting, allocation
// adjustments. Receives Layer 1 result, returns adjusted allocation + bias data.
// DOES NOT mutate Layer 1 output directly — returns a delta object.

function layer2_behavioralAI(data, core) {
  const biases        = detectBiases(data);
  const biasDelta     = biases.reduce((sum, b) => sum + b.equityAdjust, 0);
  const [lo, hi]      = core.expReturn.split("–").map(s => parseFloat(s));
  const expMidpoint   = (lo + hi) / 2;

  const branchResult  = applyBranchRules(
    data, core.alloc, core.emergencyMonths,
    core.loanRate, expMidpoint, core.netWorth,
    core.income, core.age
  );
  const { rules: branchRules, equityDelta, fixedDelta, cashDelta,
          pauseInvesting, debtFirst, estatePositioning } = branchResult;

  // Merge behavioral + branch equity deltas
  const totalEquityDelta = equityDelta + biasDelta;

  // Apply deltas to base allocation from Layer 1
  const adjusted = core.alloc.map(a => {
    if (a.cls === "Equity")            return { ...a, pct: Math.max(0, a.pct + totalEquityDelta) };
    if (a.cls === "Fixed income / Debt") return { ...a, pct: Math.max(0, a.pct + fixedDelta) };
    if (a.cls === "Cash")              return { ...a, pct: Math.max(0, a.pct + cashDelta) };
    return a;
  });

  // Normalize to 100% and fix rounding drift
  const total   = adjusted.reduce((s, a) => s + a.pct, 0);
  const normed  = adjusted.map(a => ({ ...a, pct: Math.round((a.pct / total) * 100) }));
  const drift   = 100 - normed.reduce((s, a) => s + a.pct, 0);
  if (drift !== 0) normed[normed.findIndex(a => a.cls === "Equity")].pct += drift;

  // Append estate action if triggered
  const actions = [...core.actions];
  if (estatePositioning) {
    actions.splice(-2, 0, {
      icon:"🏛️",
      title:"Review estate and tax efficiency strategy",
      detail:"Your net worth warrants a review of tax-loss harvesting, beneficiary designations, and trust structures. Consider engaging a fiduciary financial planner for this tier.",
    });
  }

  return {
    biases, biasDelta, branchRules,
    equityDelta, fixedDelta, cashDelta,
    biasTotalEquityAdjust: biasDelta,
    pauseInvesting, debtFirst, estatePositioning,
    alloc: normed,   // replaces core.alloc
    actions,         // replaces core.actions
  };
}

// ── LAYER 3: Elite / HNI Conditional Module ──────────────────────────────────
// Responsibility: complexity tier detection, stress test computation.
// Activated automatically when data signals warrant it.
// Returns tier classification and stress test data — does not alter allocation.

function layer3_eliteModule(data, core, behavioralAlloc) {
  const tier        = classifyTier(core.income, core.netWorth, core.investments, core.loanAmt, core.realEstate);
  const stressTests = computeStressTest(behavioralAlloc, core.netWorth, core.currency);
  return { tier, stressTests };
}

// ── LAYER 4: Compliance Overlay ──────────────────────────────────────────────
// Responsibility: append standardised disclaimers to the output.
// This layer NEVER modifies scores, allocations, or logic.
// It only adds the compliance footer used by FaResultView.

function layer4_complianceOverlay() {
  return {
    disclaimer: "This recommendation is for educational purposes only and does not constitute licensed financial advice. Investment values can fall as well as rise and you may receive back less than you invest. Always consult a certified financial planner or regulated investment advisor before making decisions.",
  };
}

// ── Main orchestrator — assembles all layers in order ────────────────────────
function faComputeResults(data) {
  const core       = layer1_coreEngine(data);
  const behavioral = layer2_behavioralAI(data, core);
  const elite      = layer3_eliteModule(data, core, behavioral.alloc);
  const compliance = layer4_complianceOverlay();

  return {
    // ── Layer 1 outputs ──
    savingsRate: core.savingsRate,
    emergencyMonths: core.emergencyMonths,
    dti: core.dti,
    netWorth: core.netWorth,
    currency: core.currency,
    bScore: core.bScore,
    fScore: core.fScore,
    composite: core.composite,
    profile: core.profile,
    profileDesc: core.profileDesc,
    expReturn: core.expReturn,
    expRealReturn: core.expRealReturn,
    approach: core.approach,
    volatility: core.volatility,
    rebalance: core.rebalance,
    yearsToRetire: core.yearsToRetire,
    retireAge: core.retireAge,
    loanRate: core.loanRate,
    loanAmt: core.loanAmt,
    income: core.income,
    monthlySIP: core.monthlySIP,
    savings: core.savings,
    expenses: core.expenses,
    cash: core.cash,
    investments: core.investments,
    retirement: core.retirement,
    // ── Layer 2 outputs (overrides alloc + actions from Layer 1) ──
    alloc: behavioral.alloc,
    actions: behavioral.actions,
    biases: behavioral.biases,
    branchRules: behavioral.branchRules,
    pauseInvesting: behavioral.pauseInvesting,
    debtFirst: behavioral.debtFirst,
    estatePositioning: behavioral.estatePositioning,
    biasTotalEquityAdjust: behavioral.biasTotalEquityAdjust,
    equityDelta: behavioral.equityDelta,
    fixedDelta: behavioral.fixedDelta,
    cashDelta: behavioral.cashDelta,
    // ── Layer 3 outputs ──
    tier: elite.tier,
    stressTests: elite.stressTests,
    // ── Layer 4 output ──
    disclaimer: compliance.disclaimer,
  };
}

function faFmt(n, currency) {
  const abs = Math.abs(n), sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${currency} ${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${currency} ${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${currency} ${Math.round(abs).toLocaleString()}`;
}

const FA_PROFILE_STYLES = {
  Conservative:     { bg:"#EFF6FF", text:"#1d4ed8", border:"#BFDBFE", accent:"#1d4ed8" },
  Moderate:         { bg:"#F0FDF4", text:"#15803d", border:"#BBF7D0", accent:"#16a34a" },
  "Growth-oriented":{ bg:"#FFFBEB", text:"#b45309", border:"#FDE68A", accent:"#d97706" },
  Aggressive:       { bg:"#FEF2F2", text:"#b91c1c", border:"#FECACA", accent:"#dc2626" },
};

const TIER_META = {
  1: { label:"Tier 1 — Foundation Builder",    color:"#64748b", bg:"#f8fafc", border:"#e2e8f0", desc:"Focus: Liquidity, emergency fund, and starting your investment journey with simple, low-cost index funds." },
  2: { label:"Tier 2 — Growing Professional",  color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe", desc:"Focus: Accelerating savings, maximizing tax-advantaged accounts, and diversifying across asset classes." },
  3: { label:"Tier 3 — Complex Portfolio",     color:"#7c3aed", bg:"#f5f3ff", border:"#ddd6fe", desc:"Focus: Tax efficiency, estate positioning, multi-asset allocation, and wealth preservation strategies." },
};

// ─────────────────────────────────────────────────────────────────────────────
// v2.0 — BEHAVIORAL BIAS PANEL
// ─────────────────────────────────────────────────────────────────────────────

function BiasBadges({ biases }) {
  if (!biases || biases.length === 0) return (
    <div style={{padding:"12px 16px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:10,fontSize:13,color:"#15803d",display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontSize:18}}>✅</span> No significant behavioral biases detected. Your answers suggest disciplined, rational investment decision-making.
    </div>
  );
  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {biases.map((b, i) => (
        <div key={i} style={{padding:"14px 16px",background:b.bg,border:`1px solid ${b.border}`,borderRadius:10}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <span style={{fontSize:18}}>{b.icon}</span>
            <span style={{fontSize:13,fontWeight:700,color:b.color}}>{b.type} Detected</span>
          </div>
          <p style={{fontSize:13,color:"#374151",margin:"0 0 6px",lineHeight:1.6}}>{b.description}</p>
          <p style={{fontSize:12,color:b.color,margin:0,fontWeight:600}}>⚙️ {b.adjustment}</p>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// v2.0 — PORTFOLIO STRESS TEST VIEW
// ─────────────────────────────────────────────────────────────────────────────

function StressTestView({ stressTests, currency, netWorth }) {
  const severityColor = (pct) => parseFloat(pct) <= -10 ? "#dc2626" : parseFloat(pct) <= -5 ? "#d97706" : "#16a34a";
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {stressTests.map((s, i) => (
        <div key={i} style={{border:"1px solid #e2e8f0",borderRadius:12,overflow:"hidden",background:"#fff",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
          <div style={{padding:"14px 16px",background:"#f8fafc",borderBottom:"1px solid #e2e8f0",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:"#1e293b"}}>{s.label}</div>
              <div style={{fontSize:12,color:"#64748b",marginTop:2}}>{s.desc}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:22,fontWeight:800,color:severityColor(s.displayImpact)}}>{s.displayImpact}%</div>
              <div style={{fontSize:11,color:"#94a3b8"}}>Portfolio impact</div>
            </div>
          </div>
          <div style={{padding:"14px 16px"}}>
            {netWorth > 0 && s.portfolioImpactAmt !== null && (
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:12,padding:"10px 12px",background:"#fef2f2",borderRadius:8,border:"1px solid #fecaca"}}>
                <span style={{fontSize:13,color:"#64748b",fontWeight:600}}>Estimated portfolio impact</span>
                <span style={{fontSize:14,fontWeight:800,color:"#dc2626"}}>{faFmt(s.portfolioImpactAmt, currency)}</span>
              </div>
            )}
            {/* Per-asset class impact bar */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
              {[
                {label:"Equity",impact:s.equityImpact,color:"#16a34a"},
                {label:"Fixed Income",impact:s.fixedImpact,color:"#3b82f6"},
                {label:"Gold / Alt",impact:s.goldImpact,color:"#d97706"},
                {label:"Cash",impact:s.cashImpact,color:"#64748b"},
              ].map((c, j) => (
                <div key={j} style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,padding:"8px 10px"}}>
                  <div style={{fontSize:11,color:"#94a3b8",marginBottom:3}}>{c.label}</div>
                  <div style={{fontSize:14,fontWeight:700,color:c.impact < 0 ? "#dc2626" : "#16a34a"}}>{c.impact > 0 ? "+" : ""}{(c.impact * 100).toFixed(0)}%</div>
                </div>
              ))}
            </div>
            <div style={{padding:"10px 12px",background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:8,fontSize:12,color:"#0369a1",lineHeight:1.6}}>
              🕐 <strong>Recovery expectation:</strong> {s.recoveryTime} — {s.recoveryNote}
            </div>
          </div>
        </div>
      ))}
      <div style={{padding:"10px 14px",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,fontSize:11,color:"#94a3b8",lineHeight:1.6}}>
        Stress test figures are modeled estimates based on historical asset class behavior. Actual outcomes will vary. Past patterns do not predict future events.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EXECUTIVE DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────

const EXEC_ALLOCATION_MODELS = {
  Conservative:     { equity:20, fixed:60, cash:10, gold:10 },
  Moderate:         { equity:50, fixed:30, cash:10, gold:10 },
  "Growth-oriented":{ equity:62, fixed:20, cash:8,  gold:10 },
  Aggressive:       { equity:75, fixed:10, cash:5,  gold:10 },
};

function ExecutiveDashboard({ r }) {
  const allocation = EXEC_ALLOCATION_MODELS[r.profile] || EXEC_ALLOCATION_MODELS["Moderate"];
  const currency = r.currency || "USD";
  const fmtE = (n) => faFmt(n, currency);
  const card = { background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:"24px", boxShadow:"0 4px 6px -1px rgba(0,0,0,0.08)" };
  const tierMeta = TIER_META[r.tier];
  const kpis = [
    { label:"Savings Efficiency", value:`${r.savingsRate}%`, status:r.savingsRate>=20?"#10b981":"#f59e0b" },
    { label:"Liquidity Buffer", value:`${r.emergencyMonths} Mo`, status:r.emergencyMonths>=6?"#10b981":"#ef4444" },
    { label:"Est. Net Worth", value:fmtE(r.netWorth), status:"#1e3a8a" },
    { label:"Risk Mandate", value:r.profile, status:"#6366f1" },
    { label:"Complexity Tier", value:`Tier ${r.tier}`, status:tierMeta.color },
    { label:"Expected Return", value:`${r.expReturn}/yr`, status:"#16a34a" },
  ];
  const allocRows = [
    { cls:"Equity (Global Growth)", pct:allocation.equity, rationale:"Capturing market risk premium via diversified low-cost indices.", color:"#16a34a" },
    { cls:"Fixed Income (Stability)", pct:allocation.fixed, rationale:"Capital preservation and yield generation to dampen volatility.", color:"#3b82f6" },
    { cls:"Cash / Liquid Reserves", pct:allocation.cash, rationale:"Operational buffer and dry powder for rebalancing opportunities.", color:"#64748b" },
    { cls:"Gold / Alternatives (Hedge)", pct:allocation.gold, rationale:"Systemic insurance against inflation and currency debasement.", color:"#d97706" },
  ];
  return (
    <div style={{ fontFamily:"Inter, sans-serif", color:"#111827", marginTop:"1rem" }}>
      <div style={{ borderBottom:"2px solid #1e3a8a", paddingBottom:16, marginBottom:24 }}>
        <h2 style={{ fontSize:"1.35rem", fontWeight:800, color:"#1e3a8a", margin:0 }}>Executive Wealth Strategy</h2>
        <p style={{ color:"#6b7280", marginTop:6, fontSize:13 }}>MetaVix Solutions · Wealth Strategy Summary</p>
      </div>
      <div style={{ padding:"12px 16px", background:tierMeta.bg, border:`1px solid ${tierMeta.border}`, borderRadius:10, marginBottom:20 }}>
        <span style={{ fontSize:13, fontWeight:700, color:tierMeta.color }}>{tierMeta.label}</span>
        <p style={{ fontSize:12, color:"#64748b", margin:"4px 0 0", lineHeight:1.6 }}>{tierMeta.desc}</p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:14, marginBottom:24 }}>
        {kpis.map((k,i) => (
          <div key={i} style={card}>
            <span style={{ fontSize:"0.7rem", fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.07em" }}>{k.label}</span>
            <div style={{ fontSize:"1.4rem", fontWeight:800, color:k.status, marginTop:8 }}>{k.value}</div>
          </div>
        ))}
      </div>
      <div style={card}>
        <h3 style={{ marginBottom:16, fontSize:"1rem", fontWeight:700, color:"#1e293b" }}>Strategic Asset Allocation</h3>
        <div style={{ display:"flex", height:12, borderRadius:6, overflow:"hidden", marginBottom:14, gap:2 }}>
          {allocRows.map((row,i) => (
            <div key={i} style={{ width:`${row.pct}%`, background:row.color }} title={`${row.cls}: ${row.pct}%`} />
          ))}
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.875rem" }}>
          <thead>
            <tr style={{ borderBottom:"1px solid #e5e7eb", color:"#6b7280", fontSize:"0.75rem", textTransform:"uppercase", letterSpacing:"0.06em" }}>
              <th style={{ padding:"10px 12px", textAlign:"left" }}>Asset Class</th>
              <th style={{ padding:"10px 12px", textAlign:"left" }}>Allocation</th>
              <th style={{ padding:"10px 12px", textAlign:"left" }}>Rationale</th>
            </tr>
          </thead>
          <tbody>
            {allocRows.map((row,i) => (
              <tr key={i} style={{ borderBottom:"1px solid #f1f5f9", background:i%2===0?"#fff":"#f9fafb" }}>
                <td style={{ padding:"13px 12px", fontWeight:600 }}>{row.cls}</td>
                <td style={{ padding:"13px 12px", fontWeight:700, color:row.color }}>{row.pct}%</td>
                <td style={{ padding:"13px 12px", color:"#4b5563" }}>{row.rationale}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ ...card, marginTop:16 }}>
        <h3 style={{ marginBottom:14, fontSize:"1rem", fontWeight:700, color:"#1e293b" }}>Monthly Cash Flow Snapshot</h3>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:12 }}>
          {[
            { label:"Take-Home Income", value:fmtE(r.income), color:"#16a34a" },
            { label:"Monthly Savings",  value:fmtE(r.savings||0), color:"#1d4ed8" },
            { label:"Debt-to-Income",   value:`${r.dti}%`, color:r.dti<20?"#16a34a":r.dti<35?"#d97706":"#dc2626" },
            { label:"Adjusted Return",  value:`${r.expReturn}/yr`, color:"#6366f1" },
          ].map((item,i) => (
            <div key={i} style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:8, padding:"12px 14px" }}>
              <div style={{ fontSize:"0.7rem", color:"#94a3b8", marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>{item.label}</div>
              <div style={{ fontSize:"1.15rem", fontWeight:800, color:item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>
      <p style={{ marginTop:20, fontSize:"0.72rem", color:"#9ca3af", lineHeight:1.6 }}>
        ⚠️ This executive summary is for educational purposes only and does not constitute licensed financial advice. Always consult a certified financial planner.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INVESTMENT CARD & ALLOCATION SECTION
// ─────────────────────────────────────────────────────────────────────────────

function InvestmentCard({ inv, rank }) {
  return (
    <div style={{display:"flex",gap:12,padding:"14px 0",borderBottom:"1px solid #f1f5f9"}}>
      <div style={{minWidth:28,height:28,borderRadius:"50%",background:"#f1f5f9",color:"#64748b",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>{rank}</div>
      <div style={{flex:1}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:6,marginBottom:6}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span style={{fontSize:14,fontWeight:700,color:"#1e293b"}}>{inv.name}</span>
            <span style={{fontSize:11,fontWeight:700,background:"#e2e8f0",color:"#475569",padding:"2px 8px",borderRadius:20}}>{inv.ticker}</span>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <span style={{fontSize:11,background:"#F0FDF4",color:"#15803d",padding:"2px 8px",borderRadius:20,fontWeight:600,whiteSpace:"nowrap"}}>↑ {inv.return}</span>
            <span style={{fontSize:11,background:"#f8fafc",color:"#64748b",padding:"2px 8px",borderRadius:20,whiteSpace:"nowrap"}}>Risk: {inv.risk}</span>
          </div>
        </div>
        <div style={{fontSize:13,color:"#64748b",lineHeight:1.6}}>💡 {inv.why}</div>
      </div>
    </div>
  );
}

function AllocationSection({ alloc, profile, currency, income }) {
  const [expanded, setExpanded] = useState(null);
  return (
    <div>
      <div style={{display:"flex",height:16,borderRadius:8,overflow:"hidden",marginBottom:12,gap:2}}>
        {alloc.map(a => <div key={a.cls} title={`${a.cls}: ${a.pct}%`} style={{width:`${a.pct}%`,background:a.color}}/>)}
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:10,marginBottom:24}}>
        {alloc.map(a => (
          <div key={a.cls} style={{display:"flex",alignItems:"center",gap:6,fontSize:12}}>
            <div style={{width:10,height:10,borderRadius:2,background:a.color}}/>
            <span style={{color:"#64748b"}}>{a.cls}</span>
            <span style={{fontWeight:700,color:"#1e293b"}}>{a.pct}%</span>
          </div>
        ))}
      </div>
      {alloc.map(a => {
        const examples = getInvestmentExamples(a.cls, profile);
        const monthlyAmt = income > 0 ? Math.round(income * 0.8 * (a.pct / 100)) : 0;
        const isOpen = expanded === a.cls;
        return (
          <div key={a.cls} style={{border:"1px solid #e2e8f0",borderRadius:12,marginBottom:12,overflow:"hidden",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}>
            <div onClick={() => setExpanded(isOpen ? null : a.cls)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",cursor:"pointer",background:isOpen?"#f8fafc":"#fff",userSelect:"none"}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:4,height:44,borderRadius:2,background:a.color,flexShrink:0}}/>
                <div>
                  <div style={{fontSize:15,fontWeight:700,color:"#1e293b"}}>{a.cls}</div>
                  <div style={{fontSize:12,color:"#64748b",marginTop:2}}>{a.reason}</div>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:14}}>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:24,fontWeight:800,color:a.color}}>{a.pct}%</div>
                  {income > 0 && <div style={{fontSize:11,color:"#94a3b8",marginTop:1}}>~{currency} {monthlyAmt.toLocaleString()}/mo</div>}
                </div>
                <div style={{fontSize:16,color:"#94a3b8",transition:"transform 0.2s",transform:isOpen?"rotate(180deg)":"rotate(0deg)"}}>▾</div>
              </div>
            </div>
            {isOpen && (
              <div style={{padding:"4px 16px 16px",borderTop:"1px solid #f1f5f9"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.07em",margin:"12px 0 2px"}}>
                  Top 5 recommended investments in this category
                </div>
                {examples.map((inv, i) => <InvestmentCard key={inv.ticker} inv={inv} rank={i+1}/>)}
                <div style={{marginTop:12,padding:"10px 12px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,fontSize:12,color:"#92400E",lineHeight:1.6}}>
                  ⚠️ These are examples for educational purposes. Research each option before investing. Past performance does not guarantee future results.
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FINANCE ADVISOR v2.0 — RESULT VIEW
// ─────────────────────────────────────────────────────────────────────────────

function FaResultView({ data, onRestart }) {
  const r = faComputeResults(data);
  const [viewMode, setViewMode] = useState("standard");
  const ps = FA_PROFILE_STYLES[r.profile];
  const tierMeta = TIER_META[r.tier];
  const sH = { fontSize:"0.8rem", fontWeight:700, color:"#1e3a8a", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:14, paddingBottom:10, borderBottom:"2px solid #e2e8f0" };
  const metricCards = [
    { label:"Savings rate", value:`${r.savingsRate}%`, sub:r.savingsRate>=20?"Great — keep it up!":r.savingsRate>=10?"Decent, aim for 20%":"Let's work on this", color:r.savingsRate>=20?"#16a34a":r.savingsRate>=10?"#d97706":"#dc2626" },
    { label:"Emergency fund", value:`${r.emergencyMonths} months`, sub:r.emergencyMonths>=6?"You're well protected":r.emergencyMonths>=3?"Getting there — aim for 6":"Urgent — build this first", color:r.emergencyMonths>=6?"#16a34a":r.emergencyMonths>=3?"#d97706":"#dc2626" },
    { label:"Debt-to-income", value:`${r.dti}%`, sub:r.dti===0?"Zero debt — excellent!":r.dti<20?"Healthy leverage":r.dti<35?"Manageable, monitor it":"High — reduce ASAP", color:r.dti===0?"#16a34a":r.dti<20?"#16a34a":r.dti<35?"#d97706":"#dc2626" },
    { label:"Net worth", value:faFmt(r.netWorth, r.currency), sub:r.netWorth>=0?"Positive foundation":"Focus on debt first", color:r.netWorth>=0?"#16a34a":"#dc2626" },
  ];

  return (
    <div>
      {/* View mode toggle */}
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:"1.25rem"}}>
        <div style={{display:"flex",background:"#f1f5f9",borderRadius:10,padding:3,gap:2}}>
          {[["standard","📊 Standard"],["executive","🏦 Executive"],["stress","🧪 Stress Test"]].map(([mode,label]) => (
            <button key={mode} onClick={() => setViewMode(mode)}
              style={{padding:"7px 14px",borderRadius:8,border:"none",fontSize:12,fontWeight:600,cursor:"pointer",
                background:viewMode===mode?"#1d4ed8":"transparent",
                color:viewMode===mode?"#fff":"#64748b",transition:"all 0.2s"}}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── EXECUTIVE VIEW ── */}
      {viewMode === "executive" && <ExecutiveDashboard r={r} />}

      {/* ── STRESS TEST VIEW ── */}
      {viewMode === "stress" && (
        <div>
          <div style={{textAlign:"center",marginBottom:"1.5rem",padding:"1.25rem",background:"linear-gradient(135deg,#fff1f2,#ffe4e6)",borderRadius:16,border:"1px solid #fecaca"}}>
            <div style={{fontSize:"2rem",marginBottom:6}}>🧪</div>
            <h2 style={{fontSize:"1.3rem",fontWeight:800,color:"#991b1b",marginBottom:6}}>Portfolio Stress Test</h2>
            <p style={{fontSize:13,color:"#b91c1c",lineHeight:1.7,maxWidth:480,margin:"0 auto"}}>See how your personalized portfolio would behave under different market correction scenarios, with per-asset impact and recovery guidance.</p>
          </div>
          <div style={sH}>📉 Correction Impact Analysis</div>
          <StressTestView stressTests={r.stressTests} currency={r.currency} netWorth={r.netWorth} />
        </div>
      )}

      {/* ── STANDARD VIEW ── */}
      {viewMode === "standard" && (
        <>
          {/* Hero */}
          <div style={{textAlign:"center",marginBottom:"2rem",padding:"1.5rem",background:"linear-gradient(135deg,#f0f9ff,#e0f2fe)",borderRadius:16,border:"1px solid #bae6fd"}}>
            <div style={{fontSize:"2.2rem",marginBottom:8}}>📊</div>
            <h2 style={{fontSize:"1.5rem",fontWeight:800,color:"#0c4a6e",marginBottom:6}}>Your Personalized Financial Plan</h2>
            <div style={{display:"inline-flex",alignItems:"center",gap:8,padding:"5px 14px",borderRadius:20,background:tierMeta.bg,border:`1px solid ${tierMeta.border}`,marginBottom:8}}>
              <span style={{fontSize:12,fontWeight:700,color:tierMeta.color}}>{tierMeta.label}</span>
            </div>
            <p style={{fontSize:13,color:"#0369a1",lineHeight:1.7,maxWidth:500,margin:"0 auto"}}>Tap any allocation block to explore specific investments. Use the toggle above to access the Stress Test and Executive views.</p>
          </div>

          {/* Adaptive Branch Rules */}
          {r.branchRules.length > 0 && (
            <div style={{marginBottom:"1.75rem"}}>
              <div style={sH}>⚡ Important Adjustments to Your Plan</div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {r.branchRules.map((rule, i) => {
                  const severityColors = { critical:{bg:"#fef2f2",border:"#fecaca",icon:"#dc2626",text:"#dc2626"}, high:{bg:"#fffbeb",border:"#fde68a",icon:"#d97706",text:"#92400e"}, info:{bg:"#eff6ff",border:"#bfdbfe",icon:"#1d4ed8",text:"#1e3a8a"} };
                  const sc = severityColors[rule.severity] || severityColors.info;
                  return (
                    <div key={i} style={{padding:"13px 16px",background:sc.bg,border:`1px solid ${sc.border}`,borderRadius:10,display:"flex",gap:12,alignItems:"flex-start"}}>
                      <span style={{fontSize:20,flexShrink:0,lineHeight:1,marginTop:1}}>{rule.icon}</span>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:sc.text,marginBottom:4}}>{rule.title}</div>
                        <div style={{fontSize:12,color:"#374151",lineHeight:1.65}}>{rule.detail}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Behavioral Bias Detection */}
          <div style={{marginBottom:"1.75rem"}}>
            <div style={sH}>🧠 Behavioral Bias Detection</div>
            <BiasBadges biases={r.biases} />
          </div>

          {/* Financial Health Check */}
          <div style={{marginBottom:"1.75rem"}}>
            <div style={sH}>📋 Your Financial Health Check</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
              {metricCards.map(m => (
                <div key={m.label} style={{background:"#f8fafc",borderRadius:12,padding:"1rem 1.1rem",border:"1px solid #e2e8f0"}}>
                  <div style={{fontSize:12,color:"#64748b",marginBottom:4}}>{m.label}</div>
                  <div style={{fontSize:21,fontWeight:800,color:"#1e293b"}}>{m.value}</div>
                  <div style={{fontSize:12,color:m.color,marginTop:3,fontWeight:600}}>{m.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Investor Profile */}
          <div style={{marginBottom:"1.75rem"}}>
            <div style={sH}>🎯 Your Investor Profile</div>
            <div style={{padding:"1.25rem",background:ps.bg,border:`1.5px solid ${ps.border}`,borderRadius:12}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,flexWrap:"wrap"}}>
                <span style={{padding:"5px 16px",borderRadius:20,background:"#fff",border:`1.5px solid ${ps.border}`,color:ps.text,fontSize:14,fontWeight:800}}>{r.profile} Investor</span>
                <span style={{fontSize:13,color:ps.text,fontWeight:600}}>Score: {r.composite}/100</span>
              </div>
              <p style={{fontSize:14,color:"#374151",lineHeight:1.75,margin:0}}>{r.profileDesc}</p>
              <div style={{display:"flex",gap:20,marginTop:12,paddingTop:12,borderTop:`1px solid ${ps.border}`,flexWrap:"wrap"}}>
                <div style={{fontSize:12,color:ps.text}}><strong>Behavioral score:</strong> {r.bScore}/50</div>
                <div style={{fontSize:12,color:ps.text}}><strong>Financial capacity:</strong> {r.fScore}/50</div>
                <div style={{fontSize:12,color:ps.text}}><strong>Expected return:</strong> {r.expReturn} per year</div>
                {(r.equityDelta !== 0 || r.biasTotalEquityAdjust !== 0) && (
                  <div style={{fontSize:12,color:"#d97706",fontWeight:600}}>
                    ⚙️ Equity adjusted: {r.equityDelta + r.biasTotalEquityAdjust > 0 ? "+" : ""}{r.equityDelta + r.biasTotalEquityAdjust}% from rules &amp; biases
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Portfolio Allocation */}
          <div style={{marginBottom:"1.75rem"}}>
            <div style={sH}>💼 Portfolio Allocation — Tap Each Category to See Specific Investments</div>
            <AllocationSection alloc={r.alloc} profile={r.profile} currency={r.currency} income={parseFloat(data.income)||0}/>
          </div>

          {/* Strategy */}
          <div style={{marginBottom:"1.75rem"}}>
            <div style={sH}>⚙️ Your Investment Strategy</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[
                {label:"Approach",value:r.approach,icon:"📥"},
                {label:"Volatility to expect",value:r.volatility,icon:"📉"},
                {label:"Rebalancing schedule",value:r.rebalance,icon:"🔁"},
                {label:"Tax-advantaged accounts",value:data.retirement_access==="Yes"?"Maximize these first":"Use taxable accounts",icon:"🏦"},
                {label:"Expected return (nominal)",value:r.expReturn+"/yr",icon:"📈"},
                {label:"After-inflation return",value:r.expRealReturn+"/yr",icon:"🎯"},
                {label:"Complexity tier",value:TIER_META[r.tier].label.split(" — ")[0],icon:"🏷️"},
                {label:"Depth of planning",value:r.tier===3?"Estate + Tax + Multi-asset":r.tier===2?"Tax + Diversification":"Foundation building",icon:"🗂️"},
              ].map(({ label, value, icon }) => (
                <div key={label} style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:11,color:"#94a3b8",marginBottom:4}}>{icon} {label}</div>
                  <div style={{fontSize:13,fontWeight:700,color:"#1e293b"}}>{value}</div>
                </div>
              ))}
            </div>
            {r.yearsToRetire < 10 && (
              <div style={{marginTop:12,padding:"12px 14px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,fontSize:13,color:"#92400E",lineHeight:1.6}}>
                ⚠️ <strong>Retirement alert:</strong> Only {r.yearsToRetire} years to your target retirement. Shift 10–15% more into Fixed Income to protect accumulated wealth from a late-stage market crash.
              </div>
            )}
          </div>

          {/* Action Plan */}
          <div style={{marginBottom:"1.75rem"}}>
            <div style={sH}>🚀 Your Personal Action Plan</div>
            {r.actions.map((a, i) => (
              <div key={i} style={{display:"flex",gap:14,padding:"14px",border:"1px solid #e2e8f0",borderRadius:10,marginBottom:10,background:"#fff",boxShadow:"0 1px 2px rgba(0,0,0,0.03)"}}>
                <div style={{fontSize:22,flexShrink:0,lineHeight:1,marginTop:1}}>{a.icon}</div>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:"#1e293b",marginBottom:5}}>{a.title}</div>
                  <div style={{fontSize:13,color:"#64748b",lineHeight:1.7}}>{a.detail}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer — Layer 4 Compliance Overlay */}
          <div style={{padding:"14px 16px",background:"#f8fafc",borderRadius:10,fontSize:12,color:"#94a3b8",lineHeight:1.7,border:"1px solid #e2e8f0"}}>
            <strong style={{color:"#64748b"}}>Important disclaimer:</strong> {r.disclaimer}
          </div>
        </>
      )}

      <button onClick={onRestart} style={{marginTop:"1.5rem",padding:"10px 24px",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>↩ Start over</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FINANCE ADVISOR — QUESTION FLOW
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// SHARED QUESTION-FLOW ENGINE (reused by all advisor modes)
// ─────────────────────────────────────────────────────────────────────────────

function QuestionFlow({ questions, onComplete, onBack: onBackOuter, headerTitle, headerSub, accentColor = "#1d4ed8" }) {
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [showError, setShowError] = useState(false);
  const visibleQuestions = questions.filter(q => !q.conditional || q.conditional(answers));
  const currentQuestion = visibleQuestions[currentIndex];
  const isLast = currentIndex === visibleQuestions.length - 1;
  const currentValue = currentQuestion?.type === "options" ? answers[currentQuestion?.id] || "" : inputValue;
  const handleAnswer = val => { setShowError(false); if (currentQuestion.type === "options") setAnswers(prev => ({ ...prev, [currentQuestion.id]: val })); else setInputValue(val); };
  const handleNext = () => {
    const val = currentQuestion.type === "options" ? answers[currentQuestion.id] : inputValue.trim();
    if (!val && val !== "0") { setShowError(true); return; }
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: val }));
    setShowError(false); setInputValue("");
    if (isLast) onComplete({ ...answers, [currentQuestion.id]: val }); else setCurrentIndex(i => i + 1);
  };
  const handleBack = () => {
    if (currentIndex === 0) { onBackOuter?.(); return; }
    const pi = currentIndex - 1; setCurrentIndex(pi);
    const pq = visibleQuestions[pi];
    if (pq.type !== "options") setInputValue(answers[pq.id] || "");
    setShowError(false);
  };
  const pct = visibleQuestions.length > 0 ? Math.round(((currentIndex + 1) / visibleQuestions.length) * 100) : 0;
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, [currentQuestion?.id]);
  if (!currentQuestion) return null;
  return (
    <div style={{background:"#f8fafc",color:"#1e293b",minHeight:"100vh",paddingBottom:"4rem"}}>
      <div style={{background:"linear-gradient(135deg,#1e3a8a 0%,#1e40af 60%,#1d4ed8 100%)",color:"#fff",padding:"1.5rem 1rem",marginBottom:"2rem"}}>
        <div style={{maxWidth:680,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:"1rem"}}>
          <div><h1 style={{fontSize:"1.8rem",fontWeight:700,margin:0}}>{headerTitle}</h1><p style={{color:"#bfdbfe",margin:"4px 0 0",fontSize:"0.875rem"}}>{headerSub}</p></div>
          <button onClick={onBackOuter} style={{display:"inline-flex",alignItems:"center",gap:"0.5rem",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,padding:"0.55rem 1.2rem",fontSize:"0.875rem",fontWeight:600,cursor:"pointer"}}>← Back</button>
        </div>
      </div>
      <div style={{maxWidth:680,margin:"0 auto",padding:"0 1rem"}}>
        <div style={{marginBottom:"1.5rem"}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
            <span style={{fontSize:12,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.04em"}}>Question {currentIndex+1} of {visibleQuestions.length}</span>
            <span style={{fontSize:12,color:"#64748b"}}>{pct}%</span>
          </div>
          <div style={{height:4,background:"#e2e8f0",borderRadius:2,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${pct}%`,background:"#1d4ed8",borderRadius:2,transition:"width 0.35s ease"}}/>
          </div>
        </div>
        <div style={{background:"#fff",border:"1px solid #e2e8f0",borderRadius:14,padding:"1.5rem",marginBottom:"1rem",boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
          {currentQuestion.phase !== undefined && <div style={{fontSize:11,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>{currentQuestion.phaseLabel || `Phase ${currentQuestion.phase + 1}`}</div>}
          <div style={{fontSize:17,fontWeight:600,color:"#1e293b",lineHeight:1.55,marginBottom:currentQuestion.hint?8:20}}>{currentQuestion.text}</div>
          {currentQuestion.hint && <div style={{fontSize:13,color:"#94a3b8",marginBottom:16}}>{currentQuestion.hint}</div>}
          {currentQuestion.type === "options" ? (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {currentQuestion.options.map(opt => (
                <button key={opt} onClick={() => handleAnswer(opt)}
                  style={{textAlign:"left",padding:"10px 14px",border:currentValue===opt?"1.5px solid #1d4ed8":"1px solid #e2e8f0",borderRadius:8,background:currentValue===opt?"#EFF6FF":"#fff",color:currentValue===opt?"#1d4ed8":"#374151",fontSize:14,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}>{opt}</button>
              ))}
            </div>
          ) : (
            <input ref={inputRef} type={currentQuestion.type==="number"?"number":"text"} value={currentValue} min={currentQuestion.min} max={currentQuestion.max}
              placeholder={currentQuestion.type==="number"?"Enter a number…":"Type your answer…"}
              onChange={e => handleAnswer(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleNext(); }}
              style={{width:"100%",padding:"10px 14px",border:"1px solid #e2e8f0",borderRadius:8,fontSize:15,color:"#1e293b",background:"#fff",fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}
            />
          )}
          {showError && <div style={{fontSize:13,color:"#dc2626",marginTop:8}}>Please provide an answer before continuing.</div>}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={handleBack} style={{padding:"10px 18px",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>← Back</button>
          <button onClick={handleNext}
            style={{padding:"10px 28px",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}
            onMouseEnter={e=>(e.target.style.background="#b91c1c")}
            onMouseLeave={e=>(e.target.style.background="#dc2626")}>
            {isLast ? "Get my plan →" : "Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STANDARD FINANCE ADVISOR (v2.0) — uses original QuestionFlow
// ─────────────────────────────────────────────────────────────────────────────

function FinanceAdvisor({ onBack }) {
  const [done, setDone] = useState(false);
  const [finalData, setFinalData] = useState(null);
  const handleRestart = () => { setDone(false); setFinalData(null); };
  if (done && finalData) return (
    <div style={{background:"#f8fafc",minHeight:"100vh",paddingBottom:"4rem"}}>
      <div style={{background:"linear-gradient(135deg,#1e3a8a 0%,#1e40af 60%,#1d4ed8 100%)",color:"#fff",padding:"1.5rem 1rem",marginBottom:"2rem"}}>
        <div style={{maxWidth:680,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:"1rem"}}>
          <div><h1 style={{fontSize:"1.8rem",fontWeight:700,margin:0}}>Personal Finance Advisor</h1><p style={{color:"#bfdbfe",margin:"4px 0 0",fontSize:"0.875rem"}}>MetaVix Solutions · Your Personalized Investment Strategy</p></div>
          <button onClick={onBack} style={{display:"inline-flex",alignItems:"center",gap:"0.5rem",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,padding:"0.55rem 1.2rem",fontSize:"0.875rem",fontWeight:600,cursor:"pointer"}}>← Back to AI Tools</button>
        </div>
      </div>
      <div style={{maxWidth:680,margin:"0 auto",padding:"0 1rem"}}>
        <FaResultView data={finalData} onRestart={handleRestart}/>
      </div>
    </div>
  );
  return <QuestionFlow questions={FA_QUESTIONS} onComplete={d => { setFinalData(d); setDone(true); }} onBack={onBack} headerTitle="Personal Finance Advisor" headerSub="MetaVix Solutions · Your Personalized Investment Strategy" accentColor="#1d4ed8"/>;
}

// ─────────────────────────────────────────────────────────────────────────────
// RETIREMENT ADVISOR — Questions + Calculations + Result
// ─────────────────────────────────────────────────────────────────────────────

const RETIREMENT_QUESTIONS = [
  {id:"age",phase:0,phaseLabel:"Phase 1 — Profile",text:"How old are you?",type:"number",hint:"Enter your current age.",min:18,max:85},
  {id:"retirement_age",phase:0,phaseLabel:"Phase 1 — Profile",text:"At what age do you plan to retire?",type:"number",hint:"Your target retirement age.",min:45,max:80},
  {id:"income",phase:0,phaseLabel:"Phase 1 — Profile",text:"What is your current monthly income?",type:"number",hint:"Take-home monthly income.",min:0},
  {id:"currency",phase:0,phaseLabel:"Phase 1 — Profile",text:"What currency?",type:"text",hint:"E.g. USD, GBP, EUR, INR…"},
  {id:"savings_monthly",phase:0,phaseLabel:"Phase 1 — Profile",text:"How much do you save each month toward retirement?",type:"number",hint:"Regular retirement savings per month.",min:0},
  {id:"retirement_corpus",phase:1,phaseLabel:"Phase 2 — Retirement Assets",text:"What is the current value of your retirement accounts (401k, IRA, pension)?",type:"number",hint:"Total current balance across all retirement accounts.",min:0},
  {id:"other_investments",phase:1,phaseLabel:"Phase 2 — Retirement Assets",text:"What is the value of other investments (stocks, bonds, mutual funds)?",type:"number",hint:"Non-retirement investment accounts.",min:0},
  {id:"pension_income",phase:1,phaseLabel:"Phase 2 — Retirement Assets",text:"Do you expect any pension income in retirement?",type:"options",options:["Yes","No"]},
  {id:"pension_monthly",phase:1,phaseLabel:"Phase 2 — Retirement Assets",text:"What monthly pension amount do you expect?",type:"number",hint:"Estimated monthly pension in today's dollars.",min:0,conditional:d=>d.pension_income==="Yes"},
  {id:"social_security",phase:1,phaseLabel:"Phase 2 — Retirement Assets",text:"Are you eligible for Social Security or equivalent government pension?",type:"options",options:["Yes","No","Unsure"]},
  {id:"ss_monthly",phase:1,phaseLabel:"Phase 2 — Retirement Assets",text:"What monthly Social Security / government pension do you expect?",type:"number",hint:"Estimated monthly benefit at target retirement age.",min:0,conditional:d=>d.social_security==="Yes"},
  {id:"retirement_expenses",phase:2,phaseLabel:"Phase 3 — Retirement Needs",text:"What are your estimated monthly expenses in retirement?",type:"number",hint:"In today's dollars — housing, food, healthcare, leisure.",min:0},
  {id:"healthcare_coverage",phase:2,phaseLabel:"Phase 3 — Retirement Needs",text:"Will you have healthcare coverage post-retirement?",type:"options",options:["Yes — employer/pension covered","Yes — will purchase privately","No — not arranged yet","Medicare / Government covered"]},
  {id:"risk_in_retirement",phase:2,phaseLabel:"Phase 3 — Retirement Needs",text:"What is your risk tolerance for retirement assets?",type:"options",options:["Very conservative — protect capital","Moderate — some growth needed","Balanced — income + growth","Growth — long retirement horizon"]},
];

function computeRetirement(data) {
  const age = parseInt(data.age) || 40;
  const retireAge = parseInt(data.retirement_age) || 65;
  const income = parseFloat(data.income) || 0;
  const savingsMonthly = parseFloat(data.savings_monthly) || 0;
  const corpus = parseFloat(data.retirement_corpus) || 0;
  const otherInvestments = parseFloat(data.other_investments) || 0;
  const pensionMonthly = parseFloat(data.pension_monthly) || 0;
  const ssMonthly = parseFloat(data.ss_monthly) || 0;
  const retirementExpenses = parseFloat(data.retirement_expenses) || 0;
  const currency = (data.currency || "USD").toUpperCase();

  const yearsToRetirement = Math.max(retireAge - age, 0);
  const isPreRetirement = yearsToRetirement > 0;

  // Project corpus at retirement (7% nominal growth)
  const growthRate = 0.07;
  const monthlyRate = growthRate / 12;
  const months = yearsToRetirement * 12;
  const projectedCorpus = corpus * Math.pow(1 + growthRate, yearsToRetirement) +
    (otherInvestments * Math.pow(1 + growthRate, yearsToRetirement)) +
    (months > 0 ? savingsMonthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) : 0);

  // Guaranteed income
  const guaranteedMonthly = pensionMonthly + ssMonthly;
  const incomeGap = Math.max(0, retirementExpenses - guaranteedMonthly);

  // Safe withdrawal estimate (3.5% conservative, 4% standard)
  const safeWithdrawal3_5 = projectedCorpus * 0.035 / 12;
  const safeWithdrawal4 = projectedCorpus * 0.04 / 12;

  // Retirement readiness ratio
  const targetCorpus = incomeGap * 12 / 0.04;
  const readinessRatio = targetCorpus > 0 ? (projectedCorpus / targetCorpus) * 100 : 100;
  const readinessLabel = readinessRatio >= 100 ? "On Track" : readinessRatio >= 75 ? "Needs Attention" : "Underfunded";
  const readinessColor = readinessRatio >= 100 ? "#16a34a" : readinessRatio >= 75 ? "#d97706" : "#dc2626";

  // Sequence of returns risk
  const seqRisk = retirementExpenses > safeWithdrawal4 ? "HIGH — Withdrawal rate exceeds safe range. A bad first 5 years in retirement could deplete your corpus significantly." : retirementExpenses > safeWithdrawal3_5 ? "MODERATE — Near the boundary of sustainable withdrawals. Maintain flexible spending." : "LOW — Withdrawal rate within sustainable range. Sequence risk is manageable.";

  // Income replacement ratio
  const incomeReplacement = income > 0 ? (retirementExpenses / income) * 100 : 0;

  // Bucket strategy
  const bucket1 = Math.round(retirementExpenses * 36); // 0–3 years in cash/short bonds
  const bucket2 = Math.round(projectedCorpus * 0.35);  // 3–10 years balanced
  const bucket3 = Math.max(0, Math.round(projectedCorpus - bucket1 - bucket2)); // 10+ years growth

  // Glide path / allocation
  const isPost = !isPreRetirement;
  let alloc;
  if (isPost) {
    const riskPref = data.risk_in_retirement;
    alloc = riskPref === "Very conservative — protect capital"
      ? [{cls:"Cash / Short Bonds",pct:30,color:"#64748b"},{cls:"Fixed Income",pct:45,color:"#3b82f6"},{cls:"Equity",pct:20,color:"#16a34a"},{cls:"Gold / Inflation",pct:5,color:"#d97706"}]
      : riskPref === "Moderate — some growth needed"
      ? [{cls:"Cash / Short Bonds",pct:20,color:"#64748b"},{cls:"Fixed Income",pct:40,color:"#3b82f6"},{cls:"Equity",pct:35,color:"#16a34a"},{cls:"Gold / Inflation",pct:5,color:"#d97706"}]
      : riskPref === "Growth — long retirement horizon"
      ? [{cls:"Cash / Short Bonds",pct:10,color:"#64748b"},{cls:"Fixed Income",pct:25,color:"#3b82f6"},{cls:"Equity",pct:60,color:"#16a34a"},{cls:"Gold / Inflation",pct:5,color:"#d97706"}]
      : [{cls:"Cash / Short Bonds",pct:15,color:"#64748b"},{cls:"Fixed Income",pct:35,color:"#3b82f6"},{cls:"Equity",pct:45,color:"#16a34a"},{cls:"Gold / Inflation",pct:5,color:"#d97706"}];
  } else {
    // Glide path: equity heavy early, reduce over time
    const equityPct = Math.max(30, 90 - yearsToRetirement * 1.5);
    const fixedPct = Math.min(50, Math.max(20, 100 - equityPct - 15));
    const cashPct = 5; const goldPct = Math.max(0, 100 - equityPct - fixedPct - cashPct);
    alloc = [
      {cls:"Cash",pct:cashPct,color:"#64748b"},
      {cls:"Fixed Income",pct:Math.round(fixedPct),color:"#3b82f6"},
      {cls:"Equity",pct:Math.round(equityPct),color:"#16a34a"},
      {cls:"Gold / Inflation Hedge",pct:Math.round(goldPct),color:"#d97706"},
    ];
  }

  return { age, retireAge, yearsToRetirement, isPreRetirement, currency, projectedCorpus, guaranteedMonthly, incomeGap, safeWithdrawal3_5, safeWithdrawal4, readinessRatio: readinessRatio.toFixed(0), readinessLabel, readinessColor, seqRisk, incomeReplacement: incomeReplacement.toFixed(0), targetCorpus, bucket1, bucket2, bucket3, alloc, retirementExpenses, income, savingsMonthly };
}

function RetirementResultView({ data, onRestart }) {
  const r = computeRetirement(data);
  const c = r.currency;
  const ff = (n) => faFmt(n, c);
  const sH = { fontSize:"0.8rem", fontWeight:700, color:"#065f46", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:14, paddingBottom:10, borderBottom:"2px solid #d1fae5" };
  return (
    <div>
      <div style={{textAlign:"center",marginBottom:"2rem",padding:"1.5rem",background:"linear-gradient(135deg,#ecfdf5,#d1fae5)",borderRadius:16,border:"1px solid #6ee7b7"}}>
        <div style={{fontSize:"2rem",marginBottom:6}}>🌅</div>
        <h2 style={{fontSize:"1.4rem",fontWeight:800,color:"#065f46",marginBottom:6}}>Retirement Income Planning System</h2>
        <div style={{display:"inline-flex",alignItems:"center",gap:8,padding:"4px 14px",borderRadius:20,background:r.readinessColor,color:"#fff",fontSize:12,fontWeight:700,marginBottom:8}}>{r.readinessLabel} — {r.readinessRatio}% Funded</div>
        <p style={{fontSize:13,color:"#047857",maxWidth:480,margin:"0 auto",lineHeight:1.7}}>{r.isPreRetirement ? `${r.yearsToRetirement} years until retirement — your personalized glide path and income strategy.` : "In retirement — your bucket strategy and income sustainability plan."}</p>
      </div>

      {/* Key Metrics */}
      <div style={{marginBottom:"1.75rem"}}>
        <div style={sH}>📋 Retirement Readiness Metrics</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
          {[
            {label:"Projected Corpus at Retirement",value:ff(r.projectedCorpus),color:"#16a34a"},
            {label:"Target Corpus Needed",value:ff(r.targetCorpus),color:"#1d4ed8"},
            {label:"Safe Withdrawal (3.5%)",value:`${c} ${Math.round(r.safeWithdrawal3_5).toLocaleString()}/mo`,color:"#16a34a"},
            {label:"Guaranteed Monthly Income",value:`${c} ${Math.round(r.guaranteedMonthly).toLocaleString()}/mo`,color:"#7c3aed"},
            {label:"Income Gap to Fill",value:`${c} ${Math.round(r.incomeGap).toLocaleString()}/mo`,color:r.incomeGap>0?"#dc2626":"#16a34a"},
            {label:"Income Replacement Ratio",value:`${r.incomeReplacement}%`,color:parseFloat(r.incomeReplacement)>80?"#16a34a":parseFloat(r.incomeReplacement)>60?"#d97706":"#dc2626"},
          ].map((m,i)=>(
            <div key={i} style={{background:"#f0fdf4",border:"1px solid #d1fae5",borderRadius:12,padding:"0.9rem 1.1rem"}}>
              <div style={{fontSize:11,color:"#065f46",fontWeight:700,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>{m.label}</div>
              <div style={{fontSize:15,fontWeight:800,color:m.color}}>{m.value}</div>
            </div>
          ))}
        </div>
        <div style={{marginTop:10,padding:"12px 14px",background:r.seqRisk.startsWith("HIGH")?"#fef2f2":r.seqRisk.startsWith("MOD")?"#fffbeb":"#f0fdf4",border:`1px solid ${r.seqRisk.startsWith("HIGH")?"#fecaca":r.seqRisk.startsWith("MOD")?"#fde68a":"#d1fae5"}`,borderRadius:10,fontSize:13,color:"#374151",lineHeight:1.6}}>
          <strong>Sequence-of-Returns Risk:</strong> {r.seqRisk}
        </div>
      </div>

      {/* Glide Path / Allocation */}
      <div style={{marginBottom:"1.75rem"}}>
        <div style={sH}>{r.isPreRetirement?"📉 Glide Path Allocation":"🪣 Retirement Allocation"}</div>
        <div style={{display:"flex",height:14,borderRadius:8,overflow:"hidden",marginBottom:10,gap:2}}>
          {r.alloc.map(a=><div key={a.cls} style={{width:`${a.pct}%`,background:a.color}} title={`${a.cls}: ${a.pct}%`}/>)}
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:16}}>
          {r.alloc.map(a=>(
            <div key={a.cls} style={{display:"flex",alignItems:"center",gap:5,fontSize:12}}>
              <div style={{width:9,height:9,borderRadius:2,background:a.color}}/>
              <span style={{color:"#64748b"}}>{a.cls}</span><span style={{fontWeight:700}}>{a.pct}%</span>
            </div>
          ))}
        </div>
        {r.isPreRetirement && <div style={{padding:"10px 14px",background:"#f0fdf4",border:"1px solid #d1fae5",borderRadius:8,fontSize:12,color:"#047857",lineHeight:1.6}}>📉 <strong>Glide Path:</strong> Equity allocation will gradually decrease as you approach retirement. Starting at {r.alloc.find(a=>a.cls==="Equity")?.pct}% equity today, transitioning to a more conservative mix within 5 years of retirement.</div>}
      </div>

      {/* Bucket Strategy (post-retirement or near retirement) */}
      {(!r.isPreRetirement || r.yearsToRetirement < 10) && (
        <div style={{marginBottom:"1.75rem"}}>
          <div style={sH}>🪣 Bucket Strategy Framework</div>
          {[
            {bucket:"Bucket 1",range:"0–3 years",purpose:"Immediate income",amount:r.bucket1,alloc:"Cash / Short-term bonds",color:"#64748b",bg:"#f8fafc",note:"Never invest — this is your paycheck replacement. Replenish annually from Bucket 2."},
            {bucket:"Bucket 2",range:"3–10 years",purpose:"Medium-term income",amount:r.bucket2,alloc:"Balanced (bonds + dividend equity)",color:"#3b82f6",bg:"#eff6ff",note:"Moderate growth. Refills Bucket 1 as it depletes. Target 4–7% return."},
            {bucket:"Bucket 3",range:"10+ years",purpose:"Long-term growth",amount:r.bucket3,alloc:"Growth equity, real assets",color:"#16a34a",bg:"#f0fdf4",note:"Ignore short-term volatility entirely. This is your legacy and longevity fund."},
          ].map((b,i)=>(
            <div key={i} style={{background:b.bg,border:`1px solid #e2e8f0`,borderRadius:12,padding:"14px 16px",marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8,marginBottom:8}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:b.color}}>{b.bucket} — {b.range}</div>
                  <div style={{fontSize:12,color:"#64748b",marginTop:2}}>{b.purpose} · {b.alloc}</div>
                </div>
                <div style={{fontSize:18,fontWeight:800,color:b.color}}>{ff(b.amount)}</div>
              </div>
              <div style={{fontSize:12,color:"#374151",lineHeight:1.6,paddingTop:8,borderTop:"1px solid #e2e8f0"}}>💡 {b.note}</div>
            </div>
          ))}
        </div>
      )}

      {/* Healthcare alert */}
      {data.healthcare_coverage === "No — not arranged yet" && (
        <div style={{padding:"14px 16px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:10,fontSize:13,color:"#991b1b",lineHeight:1.6,marginBottom:"1.5rem"}}>
          🏥 <strong>Healthcare Coverage Gap Detected:</strong> Post-retirement healthcare is one of the largest unplanned expenses. Obtain coverage before retiring — consider private insurance, ACA marketplace, or Medicare bridge coverage.
        </div>
      )}

      <div style={{padding:"14px 16px",background:"#f0fdf4",borderRadius:10,fontSize:12,color:"#94a3b8",lineHeight:1.7,border:"1px solid #d1fae5"}}>
        <strong style={{color:"#065f46"}}>Retirement Planning Disclaimer:</strong> Projections use assumed 7% nominal growth rate and are illustrative only. Actual results depend on market performance, inflation, and individual circumstances. Consult a licensed financial planner for personalized retirement planning.
      </div>
      <button onClick={onRestart} style={{marginTop:"1.5rem",padding:"10px 24px",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>↩ Start over</button>
    </div>
  );
}

function RetirementAdvisor({ onBack }) {
  const [done, setDone] = useState(false);
  const [finalData, setFinalData] = useState(null);
  if (done && finalData) return (
    <div style={{background:"#f8fafc",minHeight:"100vh",paddingBottom:"4rem"}}>
      <div style={{background:"linear-gradient(135deg,#1e3a8a 0%,#1e40af 60%,#1d4ed8 100%)",color:"#fff",padding:"1.5rem 1rem",marginBottom:"2rem"}}>
        <div style={{maxWidth:680,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:"1rem"}}>
          <div><h1 style={{fontSize:"1.8rem",fontWeight:700,margin:0}}>Retirement Finance Advisor</h1><p style={{color:"#bfdbfe",margin:"4px 0 0",fontSize:"0.875rem"}}>MetaVix Solutions · Your Personalized Investment Strategy</p></div>
          <button onClick={onBack} style={{display:"inline-flex",alignItems:"center",gap:"0.5rem",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,padding:"0.55rem 1.2rem",fontSize:"0.875rem",fontWeight:600,cursor:"pointer"}}>← Back</button>
        </div>
      </div>
      <div style={{maxWidth:680,margin:"0 auto",padding:"0 1rem"}}>
        <RetirementResultView data={finalData} onRestart={()=>{setDone(false);setFinalData(null);}}/>
      </div>
    </div>
  );
  return <QuestionFlow questions={RETIREMENT_QUESTIONS} onComplete={d=>{setFinalData(d);setDone(true);}} onBack={onBack} headerTitle="Retirement Finance Advisor" headerSub="MetaVix Solutions · Your Personalized Investment Strategy" accentColor="#1d4ed8"/>;
}

// ─────────────────────────────────────────────────────────────────────────────
// ELITE ADVISOR — Engine, Result View, and Component
// Config C: Layer 1 + Layer 2 + Layer 3 + Compliance
// Uses ELITE_QUESTIONS_ALL from MASTER_QUESTIONS (Phase 1–9, 40 questions)
// ─────────────────────────────────────────────────────────────────────────────

// ── Elite Tier Detection (4-tier system) ──
function eliteClassifyTier(income, netWorth) {
  const nwToIncome = income > 0 ? netWorth / income : 0;
  const isTopIncome = income > 500000;
  if (isTopIncome || nwToIncome > 10) return 4;
  if (nwToIncome > 5 || income > 200000) return 3;
  if (nwToIncome > 2 || income > 100000) return 2;
  return 1;
}

const ELITE_TIER_META = {
  1: { label:"Tier 1 — Foundation",          color:"#64748b", bg:"#f8fafc", border:"#e2e8f0",   desc:"Net worth < 2× income. Focus: fundamentals, savings discipline, and low-cost index investing." },
  2: { label:"Tier 2 — Wealth Builder",       color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe",   desc:"Net worth 2–5× income. Focus: tax-advantaged accounts, diversification, and debt optimization." },
  3: { label:"Tier 3 — Advanced Wealth",      color:"#7c3aed", bg:"#faf5ff", border:"#ddd6fe",   desc:"Net worth 5–10× income. Focus: tax efficiency, estate planning, and multi-asset allocation." },
  4: { label:"Tier 4 — Family Office Level",  color:"#b45309", bg:"#fffbeb", border:"#fde68a",   desc:"Net worth >10× income or business owner / concentrated holdings. Focus: full private wealth architecture." },
};

// ── v3.0 Behavioral Finance AI Weighting Engine ──
function eliteDetectBehavior(data) {
  const signals = [];
  let equityAdjust = 0;

  // Loss Aversion — panics or sells quickly under pressure
  const lossAverse = data.market_drop_20 === "Sell everything immediately" || data.past_decision_regret === "I panicked and sold";
  if (lossAverse) {
    signals.push({ bias:"Loss Aversion", icon:"😰", severity:"high", color:"#dc2626", bg:"#fef2f2", border:"#fecaca",
      description:"You show a strong tendency to feel losses more intensely than equivalent gains, leading to selling during downturns — the most common and costly investment mistake.",
      aiWeighting:"Equity allocation reduced by 7%. Increased fixed income buffer to reduce drawdown magnitude and limit emotional trigger points.",
      equityAdjust:-7 });
    equityAdjust -= 7;
  }

  // Overconfidence — buys more on dip + takes the bet + max growth
  const overconfident = data.market_drop_20 === "Buy more — it's an opportunity" && data.return_vs_certainty === "Take the 70/30 bet" && data.risk_preference === "Maximum growth / opportunistic";
  if (overconfident) {
    signals.push({ bias:"Overconfidence", icon:"🚀", severity:"moderate", color:"#d97706", bg:"#fffbeb", border:"#fde68a",
      description:"Your answers consistently reflect very high risk confidence. Overconfidence leads to under-diversification, over-concentration, and excessive leverage — all significant wealth destruction risks.",
      aiWeighting:"Tactical cash buffer increased by 5%. Alternatives diversification emphasized to reduce single-bet concentration.",
      equityAdjust:0 });
  }

  // Recency Bias — checks portfolio daily/multiple times + has sold and regretted
  const recencyBias = (data.check_portfolio_freq === "Multiple times daily" || data.check_portfolio_freq === "Daily") && data.past_decision_regret === "I sold some but regretted it";
  if (recencyBias) {
    signals.push({ bias:"Recency Bias", icon:"📉", severity:"moderate", color:"#7c3aed", bg:"#f5f3ff", border:"#ddd6fe",
      description:"Frequent portfolio monitoring combined with past regret-selling suggests recent market events are disproportionately influencing your decisions rather than long-term fundamentals.",
      aiWeighting:"Rebalancing schedule locked to quarterly cadence to break reactive decision cycles. Equity reduced by 3%.",
      equityAdjust:-3 });
    equityAdjust -= 3;
  }

  // Status Quo Bias — guaranteed return preference + holds when drops + never checks
  const statusQuoBias = data.return_vs_certainty === "Guaranteed 6%" && data.market_drop_20 === "Do nothing and hold" && data.check_portfolio_freq === "Monthly or less";
  if (statusQuoBias) {
    signals.push({ bias:"Status Quo / Inertia Bias", icon:"🔒", severity:"low", color:"#0284c7", bg:"#f0f9ff", border:"#bae6fd",
      description:"A preference for certainty and infrequent monitoring suggests strong inertia. This can lead to under-rebalancing and missed opportunities — a portfolio set in place and never adjusted.",
      aiWeighting:"Automated rebalancing trigger added. Tactical allocation drift limits tightened to ±5% to enforce action.",
      equityAdjust:0 });
  }

  return { signals, equityAdjust };
}

// ── Elite Advanced Metrics Engine ──
function computeEliteMetrics(data) {
  const income = parseFloat(data.income) || 0;
  const liquidAssets = parseFloat(data.liquid_assets) || 0;
  const liquidity12mo = parseFloat(data.liquidity_12mo) || 0;
  const netWorthInOne = parseFloat(data.net_worth_in_one_asset) || 0;

  // Concentration Ratio
  const concentrationRatio = netWorthInOne;
  const concLabel = concentrationRatio > 50 ? "CRITICAL" : concentrationRatio > 25 ? "HIGH" : concentrationRatio > 10 ? "MODERATE" : "LOW";
  const concColor = concentrationRatio > 50 ? "#dc2626" : concentrationRatio > 25 ? "#d97706" : concentrationRatio > 10 ? "#f59e0b" : "#16a34a";

  // Liquidity Coverage Ratio
  const lcr = liquidity12mo > 0 ? (liquidAssets / liquidity12mo) : null;
  const lcrLabel = lcr === null ? "N/A" : lcr >= 2 ? "STRONG" : lcr >= 1 ? "ADEQUATE" : "DEFICIENT";
  const lcrColor = lcr === null ? "#94a3b8" : lcr >= 2 ? "#16a34a" : lcr >= 1 ? "#d97706" : "#dc2626";

  // Tax Drag Estimate
  const taxDragPct = income > 600000 ? 42 : income > 400000 ? 37 : income > 200000 ? 32 : income > 100000 ? 26 : 20;
  const taxDragLabel = income > 400000 ? "Very High — priority: municipal bonds, tax-loss harvesting, DAF, opportunity zones" : income > 200000 ? "High — priority: maximize tax-advantaged accounts, direct indexing" : "Moderate — standard tax planning sufficient";

  // Intergenerational Capital Efficiency Score
  const hasEstate = data.estate_structures === "Yes — fully structured";
  const hasPhilanthropy = data.philanthropic !== "No";
  const hasConcentration = data.concentrated_stock === "Yes";
  let igenScore = 100;
  if (!hasEstate) igenScore -= 30;
  if (hasConcentration) igenScore -= 20;
  if (data.private_equity === "No") igenScore -= 5;
  if (!hasPhilanthropy) igenScore -= 5;
  const igenLabel = igenScore >= 85 ? "Optimized" : igenScore >= 60 ? "Partially Optimized" : "Needs Significant Work";
  const igenColor = igenScore >= 85 ? "#16a34a" : igenScore >= 60 ? "#d97706" : "#dc2626";

  // Asset Correlation Risk
  const multiAsset = [data.concentrated_stock==="Yes",data.private_equity!=="No",data.business_owner!=="No"].filter(Boolean).length;
  const corrRisk = multiAsset >= 2 ? "ELEVATED — Multiple correlated private/illiquid assets. A single macro shock could impair multiple positions simultaneously." : "MANAGEABLE — Asset base is reasonably diversified across correlation profiles.";

  return { concentrationRatio, concLabel, concColor, lcr: lcr?.toFixed(1) || "N/A", lcrLabel, lcrColor, taxDragPct, taxDragLabel, igenScore, igenLabel, igenColor, corrRisk };
}

// ── Elite Allocation Engine ──
function computeEliteAlloc(data, tier, behaviorAdjust) {
  const goal = data.primary_goal;
  const horizon = data.time_horizon;
  const riskPref = data.risk_preference;

  // Base allocation by tier and goal
  let base;
  if (tier === 4 || goal === "Intergenerational transfer" || goal === "Philanthropy & legacy") {
    base = { equity:35, fixed:20, privateMarkets:15, realAssets:12, alternatives:10, tacticalCash:8 };
  } else if (goal === "Capital preservation" || riskPref === "Capital preservation") {
    base = { equity:20, fixed:35, privateMarkets:10, realAssets:12, alternatives:10, tacticalCash:13 };
  } else if (riskPref === "Maximum growth / opportunistic") {
    base = { equity:45, fixed:10, privateMarkets:20, realAssets:10, alternatives:10, tacticalCash:5 };
  } else {
    base = { equity:38, fixed:22, privateMarkets:12, realAssets:12, alternatives:10, tacticalCash:6 };
  }

  // Apply behavioral equity adjustment
  base.equity = Math.max(5, base.equity + behaviorAdjust);
  // Rebalance: distribute delta to fixed income
  const total = Object.values(base).reduce((s,v)=>s+v,0);
  const drift = 100 - total;
  base.fixed = Math.max(0, base.fixed + drift);

  // Time horizon tilt
  if (horizon === "1–3 years")   { base.tacticalCash += 5; base.equity -= 5; }
  if (horizon === "15+ years / multi-generational") { base.privateMarkets += 5; base.tacticalCash -= 5; }

  // Normalize to 100
  const total2 = Object.values(base).reduce((s,v)=>s+v,0);
  const factor = 100 / total2;
  const norm = {};
  Object.keys(base).forEach(k => { norm[k] = Math.round(base[k] * factor); });
  const drift2 = 100 - Object.values(norm).reduce((s,v)=>s+v,0);
  norm.equity += drift2;

  return [
    {cls:"Global Equity",                   pct:norm.equity,        color:"#16a34a", purpose:"Growth engine and inflation hedge via diversified low-cost indices"},
    {cls:"Tax-Optimized Fixed Income",       pct:norm.fixed,         color:"#3b82f6", purpose:"Municipal bonds & TIPS for tax-efficient income and stability"},
    {cls:"Private Markets",                  pct:norm.privateMarkets,color:"#db2777", purpose:"Illiquidity premium — private equity, venture, and credit"},
    {cls:"Real Assets",                      pct:norm.realAssets,    color:"#7c3aed", purpose:"Property, infrastructure, timber — inflation protection"},
    {cls:"Alternatives",                     pct:norm.alternatives,  color:"#f59e0b", purpose:"Hedge funds, commodities, absolute return — non-correlation"},
    {cls:"Tactical Liquidity",               pct:norm.tacticalCash,  color:"#64748b", purpose:"Opportunity reserve and 12-month expense coverage"},
  ];
}

// ── Elite Advisory Layers Engine ──
function computeEliteLayers(data, metrics, tier) {
  const layers = [];
  const currency = (data.currency || "USD").toUpperCase();
  const income = parseFloat(data.income) || 0;
  const liquidity12mo = parseFloat(data.liquidity_12mo) || 0;

  // 1 — Concentration Risk
  if (metrics.concLabel !== "LOW") {
    layers.push({ title:"Concentration Risk Analysis", icon:"⚠️", color:"#dc2626", urgency: metrics.concLabel === "CRITICAL" ? "URGENT" : "HIGH", items:[
      `${metrics.concLabel} concentration — ${metrics.concentrationRatio}% of net worth in a single asset. Target: <10% in any single position.`,
      data.concentrated_stock === "Yes" ? "Implement a 10–15% per year staged liquidation strategy to diversify without triggering a large single-year tax event." : "Monitor asset concentration as net worth grows — rebalance when any single position exceeds 15%.",
      "Protective options strategy (collar or put) can hedge downside without requiring immediate sale.",
      tier >= 4 ? "Exchange fund participation (if eligible) allows tax-free diversification over a 7–10 year lock-up period." : "Consult a tax advisor before liquidating any large single position.",
    ]});
  }

  // 2 — Liquidity Risk Layer
  layers.push({ title:"Liquidity Risk Layer", icon:"💧", color:"#0284c7", urgency: metrics.lcrLabel === "DEFICIENT" ? "URGENT" : "STANDARD", items:[
    `Liquidity Coverage Ratio: ${metrics.lcrLabel} (${metrics.lcr}×). Liquid assets vs 12-month needs.`,
    liquidity12mo > 0 ? `Maintain ${currency} ${Math.round(liquidity12mo * 1.25).toLocaleString()} in liquid reserves (125% of 12-month plan) as your operational liquidity floor.` : "Define a 12-month cash flow forecast and earmark dedicated liquid reserves.",
    data.private_equity !== "No" ? "Private equity lock-ups (typically 5–10 years) create illiquidity risk — ensure lock-ups are not >30% of investable assets." : "No private equity lock-up risk detected.",
    "Use T-Bills or ultra-short bond ETFs for tactical cash — avoid yield drag of holding pure cash.",
  ]});

  // 3 — Tax Optimization Layer
  layers.push({ title:"Tax Optimization Layer", icon:"💰", color:"#16a34a", urgency:"STANDARD", items:[
    `Estimated tax drag: ${metrics.taxDragPct}% effective rate. ${metrics.taxDragLabel}`,
    income > 400000 ? "Maximize Qualified Opportunity Zone (QOZ) investments for capital gains deferral — powerful at this income level." : "Maximize 401k, backdoor Roth IRA, and HSA contributions before any taxable investing.",
    "Allocate fixed income to municipal bonds in taxable accounts — tax-free yield equivalent is often superior to corporate bonds after tax.",
    tier >= 3 ? "Direct indexing (owning individual stocks vs ETFs) enables systematic tax-loss harvesting at scale — can save 1–2% in annual taxes." : "Tax-loss harvest annually across ETF positions to offset capital gains.",
    data.philanthropic !== "No" ? "Donor-Advised Fund (DAF): contribute appreciated assets, claim immediate deduction, invest and grant over time. Most tax-efficient charitable vehicle." : "If philanthropy becomes a goal, a DAF is the optimal structure.",
  ]});

  // 4 — Estate Efficiency
  layers.push({ title:"Estate Efficiency Observations", icon:"🏛️", color:"#7c3aed", urgency: data.estate_structures === "Not yet" ? "URGENT" : "STANDARD", items:[
    data.estate_structures === "Not yet" ? "⚠️ CRITICAL GAP: No estate planning structures in place. Without a will or trust, your assets pass through probate — a public, costly, and time-consuming process." : data.estate_structures === "Partially in place" ? "Estate plan is partial — complete remaining documents. Review and update beneficiary designations on all accounts." : "Estate structure in place — schedule a 3-year review or upon any major life event or tax law change.",
    tier >= 3 ? "Irrevocable Life Insurance Trust (ILIT) removes life insurance proceeds from taxable estate — can save millions in estate tax at high net worth." : "Review life insurance beneficiary designations — a common and costly oversight.",
    data.philanthropic !== "No" ? "Charitable Remainder Trust (CRT) provides income during your lifetime and transfers remainder to charity — reduces estate while generating income." : "Consider Charitable Lead Trust (CLT) if philanthropy becomes a priority.",
    `Intergenerational Capital Efficiency Score: ${metrics.igenScore}/100 — ${metrics.igenLabel}`,
  ]});

  // 5 — Multi-Asset Diversification
  layers.push({ title:"Multi-Asset Diversification Strategy", icon:"🌐", color:"#0891b2", urgency:"STANDARD", items:[
    `Asset Correlation Risk: ${metrics.corrRisk}`,
    "Target maximum correlation between portfolio sleeves of 0.6 — above this, diversification benefit diminishes.",
    tier >= 3 ? "Private markets (PE, VC, private credit) historically exhibit low correlation to public equity — consider 15–25% allocation for Tier 3+ portfolios." : "Introduce REITs or infrastructure ETFs as low-correlation real asset exposure.",
    "Rebalance semi-annually or when any asset class drifts >5% from target — forces systematic buy-low sell-high.",
    "Geographic diversification: target 30–40% non-domestic equity to reduce single-country policy risk.",
  ]});

  return layers;
}

// ── Main Elite compute function ──
function computeElite(data) {
  const income = parseFloat(data.income) || 0;
  const netWorth = parseFloat(data.net_worth) || 0;
  const currency = (data.currency || "USD").toUpperCase();
  const age = parseInt(data.age) || 45;

  const tier = eliteClassifyTier(income, netWorth);
  const { signals: biasSignals, equityAdjust } = eliteDetectBehavior(data);
  const metrics = computeEliteMetrics(data);
  const alloc = computeEliteAlloc(data, tier, equityAdjust);
  const layers = computeEliteLayers(data, metrics, tier);

  // SEC-style compliance disclaimer set
  const disclaimers = [
    "This analysis is generated by an automated financial planning tool and does not constitute investment advice, a securities recommendation, or a solicitation to buy or sell any security.",
    "Past performance of any investment strategy or asset class referenced herein does not guarantee future results. All investments involve risk, including possible loss of principal.",
    "Projected returns, allocation models, and tax estimates are illustrative only and based on historical averages and user-provided inputs. Actual outcomes will differ materially.",
    "This tool does not take into account your complete financial situation. It is not a substitute for personalized advice from a licensed financial advisor, CPA, or estate attorney.",
    "MetaVix Solutions is not a registered investment advisor (RIA), broker-dealer, or fiduciary under the Investment Advisers Act of 1940 or any applicable state securities law.",
    "Users should consult with a qualified financial professional before making any investment or financial planning decisions based on this output.",
  ];

  return { income, netWorth, currency, age, tier, biasSignals, equityAdjust, metrics, alloc, layers, disclaimers };
}

// ── Elite Result View ──
function EliteResultView({ data, onRestart }) {
  const r = computeElite(data);
  const tierMeta = ELITE_TIER_META[r.tier];
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  // Standard section header — matches all other advisor result views
  const sH = { fontSize:"0.8rem", fontWeight:700, color:"#1e3a8a", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:14, paddingBottom:10, borderBottom:"2px solid #e2e8f0" };

  return (
    <div>
      {/* Hero — matches FaResultView standard style */}
      <div style={{textAlign:"center",marginBottom:"2rem",padding:"1.5rem",background:"linear-gradient(135deg,#f0f9ff,#e0f2fe)",borderRadius:16,border:"1px solid #bae6fd"}}>
        <div style={{fontSize:"2.2rem",marginBottom:8}}>👑</div>
        <h2 style={{fontSize:"1.5rem",fontWeight:800,color:"#0c4a6e",marginBottom:8}}>Your Elite Finance Report</h2>
        <div style={{display:"inline-flex",alignItems:"center",gap:8,padding:"4px 14px",borderRadius:20,background:"#1d4ed8",color:"#fff",fontSize:12,fontWeight:700,marginBottom:8}}>
          {tierMeta.label}
        </div>
        <p style={{fontSize:13,color:"#0369a1",lineHeight:1.7,maxWidth:500,margin:"0 auto"}}>Your comprehensive wealth advisory report — personalized allocation strategy, behavioral insights, capital structure analysis, and compliance disclosures.</p>
      </div>

      {/* Tier Badge */}
      <div style={{padding:"14px 16px",background:tierMeta.bg,border:`1.5px solid ${tierMeta.border}`,borderRadius:12,marginBottom:"1.75rem",display:"flex",alignItems:"flex-start",gap:12}}>
        <div style={{fontSize:20,flexShrink:0}}>🏷️</div>
        <div>
          <div style={{fontSize:14,fontWeight:800,color:tierMeta.color,marginBottom:4}}>{tierMeta.label}</div>
          <div style={{fontSize:13,color:"#374151",lineHeight:1.65}}>{tierMeta.desc}</div>
        </div>
      </div>

      {/* v3.0 Behavioral Finance AI Panel */}
      <div style={{marginBottom:"1.75rem"}}>
        <div style={sH}>🧠 Behavioral Finance AI Weighting</div>
        {r.biasSignals.length === 0 ? (
          <div style={{padding:"12px 16px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:10,fontSize:13,color:"#15803d",display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:18}}>✅</span> No significant behavioral biases detected. Your responses indicate disciplined, objective investment decision-making.
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {r.biasSignals.map((s,i) => (
              <div key={i} style={{padding:"14px 16px",background:s.bg,border:`1px solid ${s.border}`,borderRadius:10}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:6,marginBottom:6}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:18}}>{s.icon}</span>
                    <span style={{fontSize:13,fontWeight:700,color:s.color}}>{s.bias} Detected</span>
                  </div>
                  <span style={{fontSize:10,fontWeight:700,background:s.color,color:"#fff",padding:"2px 8px",borderRadius:20,textTransform:"uppercase"}}>{s.severity}</span>
                </div>
                <p style={{fontSize:12,color:"#374151",margin:"0 0 6px",lineHeight:1.65}}>{s.description}</p>
                <p style={{fontSize:11,fontWeight:700,color:s.color,margin:0}}>⚙️ AI Adjustment: {s.aiWeighting}</p>
              </div>
            ))}
            {r.equityAdjust !== 0 && (
              <div style={{padding:"8px 14px",background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,fontSize:12,color:"#1e3a8a",fontWeight:600}}>
                Net equity adjustment from behavioral AI: {r.equityAdjust > 0 ? "+" : ""}{r.equityAdjust}%
              </div>
            )}
          </div>
        )}
      </div>

      {/* Advanced Capital Metrics */}
      <div style={{marginBottom:"1.75rem"}}>
        <div style={sH}>📐 Advanced Capital Structure Metrics</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
          {[
            {label:"Concentration Ratio",   value:`${r.metrics.concLabel} (${r.metrics.concentrationRatio}%)`, color:r.metrics.concColor,  sub:"% of NW in single asset"},
            {label:"Liquidity Coverage",    value:`${r.metrics.lcrLabel} (${r.metrics.lcr}×)`,                  color:r.metrics.lcrColor,   sub:"Liquid assets ÷ 12-mo needs"},
            {label:"Est. Tax Drag",         value:`~${r.metrics.taxDragPct}%`,                                   color:"#d97706",            sub:r.metrics.taxDragLabel.split("—")[0].trim()},
            {label:"Intergenerational Eff.",value:`${r.metrics.igenLabel} (${r.metrics.igenScore}/100)`,          color:r.metrics.igenColor,  sub:"Estate & philanthropy readiness"},
          ].map((m,i) => (
            <div key={i} style={{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:12,padding:"0.9rem 1.1rem"}}>
              <div style={{fontSize:10,color:"#64748b",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>{m.label}</div>
              <div style={{fontSize:14,fontWeight:800,color:m.color,marginBottom:2}}>{m.value}</div>
              <div style={{fontSize:10,color:"#94a3b8"}}>{m.sub}</div>
            </div>
          ))}
        </div>
        <div style={{marginTop:10,padding:"10px 14px",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:8,fontSize:12,color:"#64748b",lineHeight:1.6}}>
          <strong>Asset Correlation Risk:</strong> {r.metrics.corrRisk}
        </div>
      </div>

      {/* Allocation */}
      <div style={{marginBottom:"1.75rem"}}>
        <div style={sH}>💼 Adaptive Portfolio Allocation</div>
        <div style={{display:"flex",height:14,borderRadius:8,overflow:"hidden",marginBottom:12,gap:2}}>
          {r.alloc.map(a=><div key={a.cls} style={{width:`${a.pct}%`,background:a.color}} title={`${a.cls}: ${a.pct}%`}/>)}
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
          {r.alloc.map(a=>(
            <div key={a.cls} style={{display:"flex",alignItems:"center",gap:5,fontSize:11}}>
              <div style={{width:9,height:9,borderRadius:2,background:a.color}}/> <span style={{color:"#64748b"}}>{a.cls}</span> <span style={{fontWeight:700}}>{a.pct}%</span>
            </div>
          ))}
        </div>
        <div style={{border:"1px solid #e2e8f0",borderRadius:10,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"0.85rem"}}>
            <thead><tr style={{background:"#f8fafc",borderBottom:"1px solid #e2e8f0"}}>
              {["Asset Class","Alloc.","Strategic Purpose"].map(h=><th key={h} style={{padding:"9px 12px",textAlign:"left",fontSize:"0.7rem",textTransform:"uppercase",color:"#64748b",letterSpacing:"0.06em"}}>{h}</th>)}
            </tr></thead>
            <tbody>{r.alloc.map((a,i)=>(
              <tr key={i} style={{borderBottom:"1px solid #f1f5f9",background:i%2===0?"#fff":"#f9fafb"}}>
                <td style={{padding:"11px 12px",fontWeight:600,color:"#1e293b",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{display:"inline-block",width:8,height:8,borderRadius:2,background:a.color,flexShrink:0}}></span>{a.cls}
                </td>
                <td style={{padding:"11px 12px",fontWeight:800,color:a.color,fontSize:"1rem"}}>{a.pct}%</td>
                <td style={{padding:"11px 12px",color:"#64748b",fontSize:"0.78rem"}}>{a.purpose}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>

      {/* Advisory Layers */}
      <div style={{marginBottom:"1.75rem"}}>
        <div style={sH}>🔬 Elite Advisory Layers</div>
        {r.layers.map((layer,i) => (
          <div key={i} style={{border:"1px solid #e2e8f0",borderRadius:12,marginBottom:12,overflow:"hidden"}}>
            <div style={{padding:"12px 16px",background:"#f8fafc",borderBottom:"1px solid #e2e8f0",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:18}}>{layer.icon}</span>
                <span style={{fontSize:14,fontWeight:700,color:layer.color}}>{layer.title}</span>
              </div>
              {layer.urgency !== "STANDARD" && (
                <span style={{fontSize:10,fontWeight:700,background:layer.urgency==="URGENT"?"#dc2626":"#d97706",color:"#fff",padding:"2px 8px",borderRadius:20}}>{layer.urgency}</span>
              )}
            </div>
            <div style={{padding:"12px 16px"}}>
              {layer.items.map((item,j) => (
                <div key={j} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:j<layer.items.length-1?"1px solid #f1f5f9":"none"}}>
                  <span style={{color:layer.color,flexShrink:0,marginTop:1,fontSize:12}}>→</span>
                  <span style={{fontSize:13,color:"#374151",lineHeight:1.65}}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* SEC-Style Compliance Disclaimers */}
      <div style={{border:"1px solid #e2e8f0",borderRadius:12,overflow:"hidden",marginBottom:"1.5rem"}}>
        <div onClick={() => setShowDisclaimer(v => !v)}
          style={{padding:"12px 16px",background:"#f8fafc",borderBottom:showDisclaimer?"1px solid #e2e8f0":"none",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",userSelect:"none"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:16}}>📋</span>
            <span style={{fontSize:13,fontWeight:700,color:"#1e293b"}}>SEC-Style Regulatory Disclosures</span>
          </div>
          <div style={{fontSize:14,color:"#94a3b8",transform:showDisclaimer?"rotate(180deg)":"rotate(0)",transition:"transform 0.2s"}}>▾</div>
        </div>
        {showDisclaimer && (
          <div style={{padding:"14px 16px",background:"#fff"}}>
            {r.disclaimers.map((d,i) => (
              <div key={i} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:i<r.disclaimers.length-1?"1px solid #f1f5f9":"none"}}>
                <span style={{color:"#94a3b8",flexShrink:0,fontSize:11,marginTop:2}}>{i+1}.</span>
                <span style={{fontSize:11,color:"#64748b",lineHeight:1.7}}>{d}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={onRestart} style={{marginTop:"1.5rem",padding:"10px 24px",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>↩ Start over</button>
    </div>
  );
}

function EliteAdvisor({ onBack }) {
  const [done, setDone] = useState(false);
  const [finalData, setFinalData] = useState(null);
  if (done && finalData) return (
    <div style={{background:"#f8fafc",minHeight:"100vh",paddingBottom:"4rem"}}>
      <div style={{background:"linear-gradient(135deg,#1e3a8a 0%,#1e40af 60%,#1d4ed8 100%)",color:"#fff",padding:"1.5rem 1rem",marginBottom:"2rem"}}>
        <div style={{maxWidth:720,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:"1rem"}}>
          <div>
            <h1 style={{fontSize:"1.8rem",fontWeight:700,margin:0}}>Elite Finance Advisor</h1>
            <p style={{color:"#bfdbfe",margin:"4px 0 0",fontSize:"0.875rem"}}>MetaVix Solutions · Your Personalized Investment Strategy</p>
          </div>
          <button onClick={onBack} style={{display:"inline-flex",alignItems:"center",gap:"0.5rem",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,padding:"0.55rem 1.2rem",fontSize:"0.875rem",fontWeight:600,cursor:"pointer"}}>← Back</button>
        </div>
      </div>
      <div style={{maxWidth:720,margin:"0 auto",padding:"0 1rem"}}>
        <EliteResultView data={finalData} onRestart={()=>{setDone(false);setFinalData(null);}}/>
      </div>
    </div>
  );
  return <QuestionFlow questions={ELITE_QUESTIONS_ALL} onComplete={d=>{setFinalData(d);setDone(true);}} onBack={onBack} headerTitle="Elite Finance Advisor" headerSub="MetaVix Solutions · Your Personalized Investment Strategy" accentColor="#1d4ed8"/>;
}

// ─────────────────────────────────────────────────────────────────────────────
// PREMIUM ADVISOR — Config B: Layer 1 + Layer 2 (Core + Behavioral AI)
// Uses PREMIUM_QUESTIONS from MASTER_QUESTIONS (Phase 1–8, 34 questions)
// ─────────────────────────────────────────────────────────────────────────────

function PremiumAdvisor({ onBack }) {
  const [done, setDone] = useState(false);
  const [finalData, setFinalData] = useState(null);
  const handleRestart = () => { setDone(false); setFinalData(null); };
  if (done && finalData) return (
    <div style={{background:"#f8fafc",minHeight:"100vh",paddingBottom:"4rem"}}>
      <div style={{background:"linear-gradient(135deg,#1e3a8a 0%,#1e40af 60%,#1d4ed8 100%)",color:"#fff",padding:"1.5rem 1rem",marginBottom:"2rem"}}>
        <div style={{maxWidth:680,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:"1rem"}}>
          <div>
            <h1 style={{fontSize:"1.8rem",fontWeight:700,margin:0}}>Premium Finance Advisor</h1>
            <p style={{color:"#bfdbfe",margin:"4px 0 0",fontSize:"0.875rem"}}>MetaVix Solutions · Your Personalized Investment Strategy</p>
          </div>
          <button onClick={onBack} style={{display:"inline-flex",alignItems:"center",gap:"0.5rem",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,padding:"0.55rem 1.2rem",fontSize:"0.875rem",fontWeight:600,cursor:"pointer"}}>← Back</button>
        </div>
      </div>
      <div style={{maxWidth:680,margin:"0 auto",padding:"0 1rem"}}>
        {/* Premium uses the same FaResultView — the extra behavioral questions feed
            directly into the existing layer2_behavioralAI engine via detectBiases */}
        <FaResultView data={finalData} onRestart={handleRestart}/>
      </div>
    </div>
  );
  return <QuestionFlow questions={PREMIUM_QUESTIONS} onComplete={d=>{setFinalData(d);setDone(true);}} onBack={onBack} headerTitle="Premium Finance Advisor" headerSub="MetaVix Solutions · Your Personalized Investment Strategy" accentColor="#1d4ed8"/>;
}

// ─────────────────────────────────────────────────────────────────────────────
// FINANCE ADVISOR SUITE — 4 Deployment Configurations
//
//  Config A │ Personal Finance Advisor  → Layer 1 (29 questions)
//  Config B │ Premium Finance Advisor   → Layer 1 + 2 (34 questions)
//  Config C │ Elite Finance Advisor      → Layer 1 + 2 + 3 + Compliance (40 questions)
//  Config D │ Retirement Advisor        → Specialist vertical (14 questions)
//
// All configs A/B/C share the same MASTER_QUESTIONS base — questions are
// progressively added per layer. Config D uses a separate specialist bank.
// ─────────────────────────────────────────────────────────────────────────────

function AdvisorModeSelector({ onBack }) {
  const navigate = useNavigate();
  const [activeMode, setActiveMode] = useState(null);
  const handleBack = onBack || (() => navigate("/ai-tools"));

  if (activeMode === "personal")   return <FinanceAdvisor onBack={() => setActiveMode(null)}/>;
  if (activeMode === "premium")    return <PremiumAdvisor onBack={() => setActiveMode(null)}/>;
  if (activeMode === "elite")      return <EliteAdvisor onBack={() => setActiveMode(null)}/>;
  if (activeMode === "retirement") return <RetirementAdvisor onBack={() => setActiveMode(null)}/>;

  const configs = [
    {
      id: "personal",
      name: "Personal Finance Advisor",
      description: "A complete financial health assessment with risk profiling, investment allocation, and a personalized action plan tailored to your goals.",
      image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?q=80&w=1200",
    },
    {
      id: "premium",
      name: "Premium Finance Advisor",
      description: "Everything in Personal, plus deeper analysis of your financial behavior and psychology to build a more accurate investment strategy.",
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=1200",
    },
    {
      id: "elite",
      name: "Elite Finance Advisor",
      description: "Our most comprehensive advisory — designed for complex financial situations including business ownership, concentrated assets, and estate planning.",
      image: "https://images.unsplash.com/photo-1560472355-536de3962603?q=80&w=1200",
    },
    {
      id: "retirement",
      name: "Retirement Finance Advisor",
      description: "A dedicated retirement planning assessment covering income projections, withdrawal strategy, healthcare coverage, and readiness for retirement.",
      image: "https://images.unsplash.com/photo-1559526324-593bc073d938?q=80&w=1200",
    },
  ];

  return (
    <div>
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-5 text-center" style={{position:"relative"}}>
        <h1 className="text-4xl font-bold mb-4">Finance Advisor Suite</h1>
        <p className="text-blue-200 max-w-2xl mx-auto">Choose the advisor that matches your financial situation and goals.</p>
        <div style={{position:"absolute",top:"50%",right:24,transform:"translateY(-50%)"}}>
          <button onClick={handleBack} style={{
            display:"inline-flex",alignItems:"center",gap:"0.5rem",
            background:"#dc2626",color:"#fff",border:"none",borderRadius:8,
            padding:"0.55rem 1.2rem",fontSize:"0.875rem",fontWeight:600,
            cursor:"pointer"}}>
            ← Back to AI Tools
          </button>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {configs.map(c => (
            <div key={c.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:scale-105 transition duration-300" style={{cursor:"pointer"}} onClick={() => setActiveMode(c.id)}>
              <div className="relative">
                <img src={c.image} alt={c.name} className="w-full h-48 object-cover"/>
              </div>
              <div className="p-5">
                <h3 className="text-base font-bold text-blue-800 mb-2">{c.name}</h3>
                <p className="text-gray-500 text-sm mb-4" style={{lineHeight:1.6}}>{c.description}</p>
                <button className="w-full py-2 rounded font-semibold text-sm transition bg-red-600 text-white hover:bg-red-700 cursor-pointer">
                  Explore Tool →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN AITools COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

function AITools({ defaultTool = null }) {
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState(defaultTool);

  React.useEffect(() => {
    if (defaultTool) setActiveTool(defaultTool);
  }, [defaultTool]);

  const goBack = () => { setActiveTool(null); navigate("/ai-tools"); };
  if (activeTool === "tax")     return <TaxCalculator onBack={goBack}/>;
  if (activeTool === "finance") return <AdvisorModeSelector onBack={goBack}/>;

  const tools = [
    { id:"tax",     name:"AI Tax Calculator",    description:"Estimate Federal & State tax, take-home pay, and your refund or balance due using automated logic.",           image:"https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=1200",   available:true,  badge:null,        url:"/ai-tools/tax-calculator" },
    { id:"finance", name:"Finance Advisor Suite", description:"Choose from four advisor levels — Personal, Premium, Elite, and Retirement — each delivering a fully personalized investment strategy.", image:"https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?q=80&w=1200", available:true,  badge:"4 Configs", url:"/ai-tools/finance-advisor" },
    { id:"stock",   name:"Stock Analyzer",        description:"Institutional-grade stock analysis with Market Overview, Top Gainers, Losers, Volume leaders and expert Portfolio & Hedge Fund perspective.", image:"https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?q=80&w=1200", available:true,  badge:null,        url:"/ai-tools/stock-analyzer" },
    { id:"kpi",     name:"KPI Dashboard Builder", description:"Build and visualize key performance indicators in real time with smart analytics.",                            image:"https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?q=80&w=1200", available:false, badge:null,        url:null },
  ];

  const pageTitle = "AI Tools | MetaVix Solutions — Tax Calculator, Finance Advisor & More";
  const pageDescription = "Explore MetaVix Solutions' free AI-powered tools: AI Tax Calculator for Federal & State tax estimates, Finance Advisor Suite with personal, premium, elite and retirement planning, Calories Count Tool, and KPI Dashboard Builder.";
  const pageUrl = "https://www.metavixsolutions.com/ai-tools";
  const logoUrl = "https://www.metavixsolutions.com/Metavix-logo.png";

  return (
    <>
      {/* ── SEO & Social Meta Tags ── */}
      <Helmet>
        {/* Primary */}
        <title>{pageTitle}</title>
        <link rel="canonical" href={`https://www.metavixsolutions.com/ai-tools`} />
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="AI tax calculator, federal tax estimator, state tax calculator, finance advisor, investment strategy, retirement planning, KPI dashboard, MetaVix Solutions" />
        <link rel="canonical" href={pageUrl} />
        <meta name="robots" content="index, follow" />

        {/* Open Graph (Facebook, LinkedIn) */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={logoUrl} />
        <meta property="og:site_name" content="MetaVix Solutions" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={logoUrl} />

        {/* Google Rich Results — ItemList structured data */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "AI Tools by MetaVix Solutions",
          "description": pageDescription,
          "url": pageUrl,
          "numberOfItems": 4,
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "AI Tax Calculator",
              "description": "Free AI-powered Federal and State tax estimator. Calculate take-home pay, effective tax rate, marginal rate, and refund or balance due.",
              "url": pageUrl
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Finance Advisor Suite",
              "description": "Personalized investment strategy tool with four advisor levels: Personal, Premium, Elite, and Retirement Finance Advisor.",
              "url": pageUrl
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Calories Count Tool",
              "description": "Calculate daily calorie requirements, track macros, and analyze nutrition metrics. Coming soon.",
              "url": pageUrl
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "KPI Dashboard Builder",
              "description": "Build and visualize key performance indicators in real time with smart analytics. Coming soon.",
              "url": pageUrl
            }
          ]
        })}</script>

        {/* Google Rich Results — SoftwareApplication structured data */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "AI Tax Calculator",
          "url": pageUrl,
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          "description": "Free AI-powered Federal and State tax estimator by MetaVix Solutions. Calculate take-home pay, refund or balance due, effective tax rate, and marginal rate.",
          "provider": {
            "@type": "Organization",
            "name": "MetaVix Solutions",
            "url": "https://www.metavixsolutions.com"
          }
        })}</script>
      </Helmet>

      {/* ── Page content ── */}
      <main>
        {/* Hero section — h1 is the primary page heading for SEO */}
        <section
          className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-5 text-center"
          aria-label="AI Tools overview"
        >
          <h1 className="text-4xl font-bold mb-4">AI Tools</h1>
          <p className="text-blue-200 max-w-2xl mx-auto">
            Explore our suite of AI-powered tools designed to accelerate your business transformation.
          </p>
        </section>

        {/* Tools grid */}
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            role="list"
            aria-label="Available AI tools"
          >
            {tools.map(tool => (
              <article
                key={tool.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:scale-105 transition duration-300"
                role="listitem"
                aria-label={tool.name}
              >
                <div className="relative">
                  <img
                    src={tool.image}
                    alt={`${tool.name} — ${tool.description}`}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                    width="400"
                    height="192"
                  />
                  {!tool.available && (
                    <div className="absolute top-3 right-3 bg-gray-800 bg-opacity-75 text-white text-xs font-semibold px-2 py-1 rounded">
                      Coming Soon
                    </div>
                  )}
                  {tool.badge && (
                    <div className="absolute top-3 left-3 bg-blue-700 text-white text-xs font-semibold px-2 py-1 rounded">
                      {tool.badge}
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h2 className="text-base font-bold text-blue-800 mb-2">{tool.name}</h2>
                  <p className="text-gray-500 text-sm mb-4" style={{lineHeight:1.6}}>
                    {tool.description}
                  </p>
                  <button
                    onClick={() => {
                      if (!tool.available) return;
                      if (tool.id === "stock")   navigate("/ai-tools/stock-analyzer");
                      else if (tool.id === "finance") navigate("/ai-tools/finance-advisor");
                      else if (tool.id === "tax")     navigate("/ai-tools/tax-calculator");
                    }}
                    className={`w-full py-2 rounded font-semibold text-sm transition ${tool.available ? "bg-red-600 text-white hover:bg-red-700 cursor-pointer" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                    disabled={!tool.available}
                    aria-label={tool.available ? `Open ${tool.name}` : `${tool.name} — Coming Soon`}
                  >
                    {tool.available ? "Explore Tool →" : "Coming Soon"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}

export default AITools;